import React, { useState, useEffect } from 'react';
import { Package, Truck, Calendar, Clock, DollarSign, MapPin, Plus, X, Save, Settings, AlertCircle, CheckCircle, ChevronDown, ChevronUp, Edit2, CreditCard } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Barcode Management Section Component
const BarcodeManagementSection = () => {
  const [barcodes, setBarcodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTableBarcodes, setLoadingTableBarcodes] = useState(false);
  const [numTables, setNumTables] = useState(10);
  const [startingNumber, setStartingNumber] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('success'); // 'success' or 'error'
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    fetchBarcodes();
  }, []);

  const fetchBarcodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/pay-at-table/barcodes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setBarcodes(data.barcodes || []);
      }
    } catch (error) {
      console.error('Error fetching barcodes:', error);
    }
  };

  const showMessage = (message, type = 'success') => {
    setModalMessage(message);
    setModalType(type);
    setShowMessageModal(true);
  };

  const handleGenerateBarcodes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const generatedCount = [];
      const errors = [];
      
      for (let i = 0; i < numTables; i++) {
        const barcodeNumber = startingNumber + i;
        const response = await fetch(`${API_BASE_URL}/api/pay-at-table/barcodes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ barcode_number: barcodeNumber })
        });
        
        const data = await response.json();
        if (response.ok && data.success) {
          generatedCount.push(data.barcode);
        } else {
          errors.push(`Barcode #${barcodeNumber}: ${data.message || 'Failed'}`);
        }
      }
      
      if (generatedCount.length > 0) {
        showMessage(`Successfully generated ${generatedCount.length} barcode(s)!`, 'success');
        fetchBarcodes();
      } else {
        showMessage(`Failed to generate barcodes.\n${errors.join('\n')}`, 'error');
      }
    } catch (error) {
      console.error('Error generating barcodes:', error);
      showMessage('Error generating barcodes. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (barcodeId) => {
    setPendingDelete(barcodeId);
    setShowConfirmModal(true);
  };

  const handleDeleteBarcode = async () => {
    if (!pendingDelete) return;
    
    setShowConfirmModal(false);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/pay-at-table/barcodes/${pendingDelete}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        showMessage('Barcode deleted successfully', 'success');
        fetchBarcodes();
      } else {
        showMessage(data.message || 'Error deleting barcode', 'error');
      }
    } catch (error) {
      console.error('Error deleting barcode:', error);
      showMessage('Error deleting barcode', 'error');
    } finally {
      setPendingDelete(null);
    }
  };

  const handleDownloadBarcode = (barcode, format = 'png') => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${barcode.qr_code_image}`;
    link.download = `pay-at-table-barcode-${barcode.barcode_number}.png`;
    link.click();
  };

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Barcode Management</h3>
        <p className="text-sm text-gray-600">Generate and manage barcodes for Pay at Table</p>
      </div>
      
      <div className="space-y-4">
        {/* Generate Barcodes Section */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Generate Barcodes</h4>
              <p className="text-xs text-gray-600 mt-1">Create new barcodes for your tables</p>
            </div>
            <button 
              onClick={handleGenerateBarcodes}
              disabled={loading}
              className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate'}
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Number of Barcodes</label>
              <input 
                type="number" 
                min="1" 
                max="100" 
                value={numTables}
                onChange={(e) => setNumTables(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Starting Number</label>
              <input 
                type="number" 
                min="1" 
                value={startingNumber}
                onChange={(e) => setStartingNumber(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* List of Barcodes */}
        {barcodes.length > 0 && (
          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Generated Barcodes ({barcodes.length})</h4>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {barcodes.map((barcode) => (
                <div key={barcode.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <img 
                    src={`data:image/png;base64,${barcode.qr_code_image}`}
                    alt={`Barcode ${barcode.barcode_number}`}
                    className="w-16 h-16 border border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Barcode #{barcode.barcode_number}</p>
                    <p className="text-xs text-gray-600 break-all">{barcode.url}</p>
                    {barcode.assigned_table_name && (
                      <p className="text-xs text-theme-blue mt-1">Assigned: {barcode.assigned_table_name}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownloadBarcode(barcode)}
                      className="px-3 py-1 text-xs bg-theme-blue text-white rounded hover:bg-blue-700"
                    >
                      Download
                    </button>
                    <button
                      onClick={() => confirmDelete(barcode.id)}
                      className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="text-sm font-medium text-yellow-900 mb-2">Setup Instructions</h4>
          <ol className="text-xs text-yellow-800 space-y-1 list-decimal list-inside">
            <li>Generate barcodes using the form above</li>
            <li>Download and print each barcode</li>
            <li>Assign barcodes to tables from the Orders Dashboard</li>
            <li>Place printed barcodes on their assigned tables</li>
            <li>Customers scan to directly access payment for their table</li>
          </ol>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Confirm Delete</h3>
              <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete this barcode? This action cannot be undone.</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPendingDelete(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteBarcode}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="p-5">
              <div className="flex items-start gap-3 mb-3">
                {modalType === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {modalType === 'success' ? 'Success' : 'Error'}
                  </h3>
                  <p className="text-sm text-gray-600 whitespace-pre-line">{modalMessage}</p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ECommerceSettings = () => {
  const [activeTab, setActiveTab] = useState('orderNowPickup');
  const [showAddAreaModal, setShowAddAreaModal] = useState(false);
  const [newZipCode, setNewZipCode] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saving, setSaving] = useState(false);
  const [loadingTableBarcodes, setLoadingTableBarcodes] = useState(false);
  
  // Confirmation modals for order management
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');

  // Pickup locations state
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [currentLocationTab, setCurrentLocationTab] = useState(''); // 'orderNow' or 'preOrder'
  const [locationForm, setLocationForm] = useState({
    name: '',
    address: '',
    phone: ''
  });

  // Country and State management
  const [showAddCountryModal, setShowAddCountryModal] = useState(false);
  const [showAddStateModal, setShowAddStateModal] = useState(false);
  const [selectedCountryIndex, setSelectedCountryIndex] = useState(null);
  const [newCountryName, setNewCountryName] = useState('');
  const [newStateName, setNewStateName] = useState('');
  const [openCountryAccordion, setOpenCountryAccordion] = useState(null);
  
  // Category shipping management
  const [showAddCategoryShippingModal, setShowAddCategoryShippingModal] = useState(false);
  const [menuCategories, setMenuCategories] = useState([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState('');
  const [selectedProductCategory, setSelectedProductCategory] = useState('');
  const [categoryShippingFee, setCategoryShippingFee] = useState(0);
  
  // Flat shipping options management
  const [showAddFlatShippingModal, setShowAddFlatShippingModal] = useState(false);
  const [editingFlatShippingIndex, setEditingFlatShippingIndex] = useState(null);
  const [flatShippingName, setFlatShippingName] = useState('');
  const [flatShippingPrice, setFlatShippingPrice] = useState(0);
  const [flatShippingDescription, setFlatShippingDescription] = useState('');
  
  // Workflow management
  const [showAddWorkflowStepModal, setShowAddWorkflowStepModal] = useState(false);
  const [editingWorkflowStepIndex, setEditingWorkflowStepIndex] = useState(null);
  const [currentWorkflowTab, setCurrentWorkflowTab] = useState(''); // which order type we're editing
  const [workflowStepLabel, setWorkflowStepLabel] = useState('');
  const [isPaymentStep, setIsPaymentStep] = useState(false);

  // PIN Management
  const [currentPIN, setCurrentPIN] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    orderNowPickup: {
      enabled: true,
      prepTime: 30,
      pickupLocations: [],
      orderPrefix: 'ONP',
      orderCounter: 1000,
      workflow: [
        { id: '1', label: 'Received', isFirst: true, isLast: false },
        { id: '2', label: 'Preparing', isFirst: false, isLast: false },
        { id: '3', label: 'Ready for Pickup', isFirst: false, isLast: false },
        { id: '4', label: 'Completed', isFirst: false, isLast: true }
      ]
    },
    orderNowDelivery: {
      enabled: true,
      prepTime: 30,
      deliveryTime: 30,
      deliveryFeeType: 'free', // 'free' or 'charge'
      deliveryFee: 0,
      deliveryAreas: [],
      orderPrefix: 'OND',
      orderCounter: 1000,
      workflow: [
        { id: '1', label: 'Received', isFirst: true, isLast: false },
        { id: '2', label: 'Preparing', isFirst: false, isLast: false },
        { id: '3', label: 'Out for Delivery', isFirst: false, isLast: false },
        { id: '4', label: 'Delivered', isFirst: false, isLast: true }
      ]
    },
    preOrderPickup: {
      enabled: false,
      pickupOption: 'oneDayBefore', // 'oneDayBefore', 'sameDay', 'dualCutoff'
      cateringPrepTime: 24,
      discountPercentage: 0,
      pickupLocations: [],
      orderPrefix: 'POP',
      orderCounter: 1000,
      workflow: [
        { id: '1', label: 'Received', isFirst: true, isLast: false },
        { id: '2', label: 'Confirmed', isFirst: false, isLast: false },
        { id: '3', label: 'Preparing', isFirst: false, isLast: false },
        { id: '4', label: 'Ready for Pickup', isFirst: false, isLast: false },
        { id: '5', label: 'Completed', isFirst: false, isLast: true }
      ],
      oneDayBefore: {
        cutoffTime: '06:00',
        pickupStartTime: '06:00',
        pickupEndTime: '18:00'
      },
      sameDay: {
        cutoffTime: '06:00',
        pickupStartTime: '06:00',
        pickupEndTime: '18:00'
      },
      dualCutoff: {
        morningEnabled: true,
        morningCutoffTime: '06:00',
        morningPickupStartTime: '06:00',
        morningPickupEndTime: '12:00',
        eveningEnabled: true,
        eveningCutoffTime: '12:00',
        eveningPickupStartTime: '17:00',
        eveningPickupEndTime: '21:00'
      }
    },
    preOrderDelivery: {
      enabled: false,
      deliveryOption: 'oneDayBefore', // 'oneDayBefore', 'sameDay', 'dualCutoff'
      deliveryFeeType: 'free',
      deliveryFee: 0,
      deliveryAreas: [],
      cateringPrepTime: 24,
      discountPercentage: 0,
      orderPrefix: 'POD',
      orderCounter: 1000,
      workflow: [
        { id: '1', label: 'Received', isFirst: true, isLast: false },
        { id: '2', label: 'Confirmed', isFirst: false, isLast: false },
        { id: '3', label: 'Preparing', isFirst: false, isLast: false },
        { id: '4', label: 'Out for Delivery', isFirst: false, isLast: false },
        { id: '5', label: 'Delivered', isFirst: false, isLast: true }
      ],
      oneDayBefore: {
        cutoffTime: '06:00',
        deliveryStartTime: '06:00',
        deliveryEndTime: '18:00'
      },
      sameDay: {
        cutoffTime: '06:00',
        deliveryStartTime: '06:00',
        deliveryEndTime: '18:00'
      },
      dualCutoff: {
        morningEnabled: true,
        morningCutoffTime: '06:00',
        morningDeliveryStartTime: '06:00',
        morningDeliveryEndTime: '12:00',
        eveningEnabled: true,
        eveningCutoffTime: '12:00',
        eveningDeliveryStartTime: '17:00',
        eveningDeliveryEndTime: '21:00'
      }
    },
    orderAtCounter: {
      enabled: false,
      orderPrefix: 'OAC',
      orderCounter: 1000,
      workflow: [
        { id: '1', label: 'Received', isFirst: true, isLast: false },
        { id: '2', label: 'In Progress', isFirst: false, isLast: false },
        { id: '3', label: 'Completed', isFirst: false, isLast: true }
      ]
    },
    orderAtTable: {
      enabled: false,
      enablePayments: true,
      enablePIN: false,
      enableBarcodeScanning: false,
      pinType: 'daily', // 'daily' or 'manual'
      orderPrefix: 'OAT',
      orderCounter: 1000,
      enableTip: true,
      tipOptions: [10, 15, 20],
      workflow: [
        { id: '1', label: 'Received', isFirst: true, isLast: false },
        { id: '2', label: 'Preparing', isFirst: false, isLast: false },
        { id: '3', label: 'Ready', isFirst: false, isLast: false },
        { id: '4', label: 'Completed', isFirst: false, isLast: true }
      ],
      workflowPIN: [
        { id: '1', label: 'Received', isFirst: true, isLast: false },
        { id: '2', label: 'Preparing', isFirst: false, isLast: false },
        { id: '3', label: 'Ready', isFirst: false, isLast: false },
        { id: '4', label: 'Awaiting Payment', isFirst: false, isLast: false },
        { id: '5', label: 'Completed', isFirst: false, isLast: true }
      ]
    },
    payAtTable: {
      enabled: false,
      enableTableSelection: true,
      enableBarcodeScanning: false
    },
    shipping: {
      enabled: false,
      orderPrefix: 'SHP',
      orderCounter: 1000,
      workflow: [
        { id: '1', label: 'Received', isFirst: true, isLast: false },
        { id: '2', label: 'Processing', isFirst: false, isLast: false },
        { id: '3', label: 'In Transit', isFirst: false, isLast: false },
        { id: '4', label: 'Delivered', isFirst: false, isLast: true }
      ],
      orderNumberPrefix: 'SHIP',
      orderNumberStartFrom: 1000,
      shippingType: 'free',
      flatShippingOptions: [
        { name: 'Regular Shipping', price: 5.00, description: 'Arrives in 5-7 business days' },
        { name: 'Priority Shipping', price: 10.00, description: 'Arrives in 3-5 business days' },
        { name: 'Express Shipping', price: 20.00, description: 'Arrives in 1-2 business days' }
      ],
      freeShippingDescription: 'Free shipping on all orders',
      categoryShipping: [],
      categoryShippingDescription: 'Shipping fee varies by product category',
      categoryDefaultType: 'free',
      categoryDefaultFee: 0,
      countries: [
        {
          name: 'USA',
          enabled: true,
          states: [
            { name: 'Alabama', enabled: true }, { name: 'Alaska', enabled: true }, { name: 'Arizona', enabled: true },
            { name: 'Arkansas', enabled: true }, { name: 'California', enabled: true }, { name: 'Colorado', enabled: true },
            { name: 'Connecticut', enabled: true }, { name: 'Delaware', enabled: true }, { name: 'Florida', enabled: true },
            { name: 'Georgia', enabled: true }, { name: 'Hawaii', enabled: true }, { name: 'Idaho', enabled: true },
            { name: 'Illinois', enabled: true }, { name: 'Indiana', enabled: true }, { name: 'Iowa', enabled: true },
            { name: 'Kansas', enabled: true }, { name: 'Kentucky', enabled: true }, { name: 'Louisiana', enabled: true },
            { name: 'Maine', enabled: true }, { name: 'Maryland', enabled: true }, { name: 'Massachusetts', enabled: true },
            { name: 'Michigan', enabled: true }, { name: 'Minnesota', enabled: true }, { name: 'Mississippi', enabled: true },
            { name: 'Missouri', enabled: true }, { name: 'Montana', enabled: true }, { name: 'Nebraska', enabled: true },
            { name: 'Nevada', enabled: true }, { name: 'New Hampshire', enabled: true }, { name: 'New Jersey', enabled: true },
            { name: 'New Mexico', enabled: true }, { name: 'New York', enabled: true }, { name: 'North Carolina', enabled: true },
            { name: 'North Dakota', enabled: true }, { name: 'Ohio', enabled: true }, { name: 'Oklahoma', enabled: true },
            { name: 'Oregon', enabled: true }, { name: 'Pennsylvania', enabled: true }, { name: 'Rhode Island', enabled: true },
            { name: 'South Carolina', enabled: true }, { name: 'South Dakota', enabled: true }, { name: 'Tennessee', enabled: true },
            { name: 'Texas', enabled: true }, { name: 'Utah', enabled: true }, { name: 'Vermont', enabled: true },
            { name: 'Virginia', enabled: true }, { name: 'Washington', enabled: true }, { name: 'West Virginia', enabled: true },
            { name: 'Wisconsin', enabled: true }, { name: 'Wyoming', enabled: true }
          ]
        },
        {
          name: 'India',
          enabled: true,
          states: [
            { name: 'Andhra Pradesh', enabled: true }, { name: 'Arunachal Pradesh', enabled: true },
            { name: 'Assam', enabled: true }, { name: 'Bihar', enabled: true }, { name: 'Chhattisgarh', enabled: true },
            { name: 'Goa', enabled: true }, { name: 'Gujarat', enabled: true }, { name: 'Haryana', enabled: true },
            { name: 'Himachal Pradesh', enabled: true }, { name: 'Jharkhand', enabled: true }, { name: 'Karnataka', enabled: true },
            { name: 'Kerala', enabled: true }, { name: 'Madhya Pradesh', enabled: true }, { name: 'Maharashtra', enabled: true },
            { name: 'Manipur', enabled: true }, { name: 'Meghalaya', enabled: true }, { name: 'Mizoram', enabled: true },
            { name: 'Nagaland', enabled: true }, { name: 'Odisha', enabled: true }, { name: 'Punjab', enabled: true },
            { name: 'Rajasthan', enabled: true }, { name: 'Sikkim', enabled: true }, { name: 'Tamil Nadu', enabled: true },
            { name: 'Telangana', enabled: true }, { name: 'Tripura', enabled: true }, { name: 'Uttar Pradesh', enabled: true },
            { name: 'Uttarakhand', enabled: true }, { name: 'West Bengal', enabled: true },
            { name: 'Andaman and Nicobar Islands', enabled: true }, { name: 'Chandigarh', enabled: true },
            { name: 'Dadra and Nagar Haveli and Daman and Diu', enabled: true }, { name: 'Delhi', enabled: true },
            { name: 'Jammu and Kashmir', enabled: true }, { name: 'Ladakh', enabled: true }, { name: 'Lakshadweep', enabled: true },
            { name: 'Puducherry', enabled: true }
          ]
        }
      ]
    }
  });

  // New area form state
  const [newArea, setNewArea] = useState({
    cityName: '',
    zipCodes: []
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSettings();
    fetchMenuCategories();
  }, []);

  useEffect(() => {
    if (activeTab === 'orderAtTable' && settings.orderAtTable.enablePIN) {
      fetchCurrentPIN();
    }
  }, [activeTab, settings.orderAtTable.enablePIN]);

  const fetchMenuCategories = async () => {
    try {
      // Fetch menu categories (tabs)
      const categoriesResponse = await fetch(`${API_BASE_URL}/api/menu-categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (categoriesResponse.ok) {
        const categories = await categoriesResponse.json();
        console.log('Fetched menu categories:', categories);
        
        // Fetch menu items to get product categories
        const itemsResponse = await fetch(`${API_BASE_URL}/api/menu-items`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (itemsResponse.ok) {
          const items = await itemsResponse.json();
          console.log('Fetched menu items:', items);
          
          // Group items by menu_category_id and extract unique product categories
          const categoriesWithProducts = categories.map(category => {
            const categoryItems = items.filter(item => item.menu_category_id === category.id);
            const uniqueProductCategories = [...new Set(categoryItems.map(item => item.category))].filter(Boolean);
            
            return {
              ...category,
              productCategories: uniqueProductCategories
            };
          });
          
          console.log('Categories with product categories:', categoriesWithProducts);
          setMenuCategories(categoriesWithProducts);
        }
      } else {
        console.error('Failed to fetch menu categories:', categoriesResponse.status);
      }
    } catch (error) {
      console.error('Error fetching menu categories:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ecommerce-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          // Ensure preOrderPickup and preOrderDelivery have the new structure
          const updatedData = {
            ...data,
            orderNowPickup: {
              ...data.orderNowPickup,
              pickupLocations: data.orderNowPickup?.pickupLocations || [],
              workflow: data.orderNowPickup?.workflow || [
                { id: '1', label: 'Received', isFirst: true, isLast: false },
                { id: '2', label: 'Preparing', isFirst: false, isLast: false },
                { id: '3', label: 'Ready for Pickup', isFirst: false, isLast: false },
                { id: '4', label: 'Completed', isFirst: false, isLast: true }
              ]
            },
            orderNowDelivery: {
              ...data.orderNowDelivery,
              workflow: data.orderNowDelivery?.workflow || [
                { id: '1', label: 'Received', isFirst: true, isLast: false },
                { id: '2', label: 'Preparing', isFirst: false, isLast: false },
                { id: '3', label: 'Out for Delivery', isFirst: false, isLast: false },
                { id: '4', label: 'Delivered', isFirst: false, isLast: true }
              ]
            },
            preOrderPickup: {
              enabled: data.preOrderPickup?.enabled || false,
              pickupOption: data.preOrderPickup?.pickupOption || 'oneDayBefore',
              cateringPrepTime: data.preOrderPickup?.cateringPrepTime || 24,
              discountPercentage: data.preOrderPickup?.discountPercentage || 0,
              pickupLocations: data.preOrderPickup?.pickupLocations || [],
              workflow: data.preOrderPickup?.workflow || [
                { id: '1', label: 'Received', isFirst: true, isLast: false },
                { id: '2', label: 'Confirmed', isFirst: false, isLast: false },
                { id: '3', label: 'Preparing', isFirst: false, isLast: false },
                { id: '4', label: 'Ready for Pickup', isFirst: false, isLast: false },
                { id: '5', label: 'Completed', isFirst: false, isLast: true }
              ],
              oneDayBefore: data.preOrderPickup?.oneDayBefore || {
                cutoffTime: '06:00',
                pickupStartTime: '06:00',
                pickupEndTime: '18:00'
              },
              sameDay: data.preOrderPickup?.sameDay || {
                cutoffTime: '06:00',
                pickupStartTime: '06:00',
                pickupEndTime: '18:00'
              },
              dualCutoff: data.preOrderPickup?.dualCutoff || {
                morningEnabled: true,
                morningCutoffTime: '06:00',
                morningPickupStartTime: '06:00',
                morningPickupEndTime: '12:00',
                eveningEnabled: true,
                eveningCutoffTime: '12:00',
                eveningPickupStartTime: '17:00',
                eveningPickupEndTime: '21:00'
              }
            },
            preOrderDelivery: {
              enabled: data.preOrderDelivery?.enabled || false,
              deliveryOption: data.preOrderDelivery?.deliveryOption || 'oneDayBefore',
              deliveryFeeType: data.preOrderDelivery?.deliveryFeeType || 'free',
              deliveryFee: data.preOrderDelivery?.deliveryFee || 0,
              deliveryAreas: data.preOrderDelivery?.deliveryAreas || [],
              cateringPrepTime: data.preOrderDelivery?.cateringPrepTime || 24,
              discountPercentage: data.preOrderDelivery?.discountPercentage || 0,
              workflow: data.preOrderDelivery?.workflow || [
                { id: '1', label: 'Received', isFirst: true, isLast: false },
                { id: '2', label: 'Confirmed', isFirst: false, isLast: false },
                { id: '3', label: 'Preparing', isFirst: false, isLast: false },
                { id: '4', label: 'Out for Delivery', isFirst: false, isLast: false },
                { id: '5', label: 'Delivered', isFirst: false, isLast: true }
              ],
              oneDayBefore: data.preOrderDelivery?.oneDayBefore || {
                cutoffTime: '06:00',
                deliveryStartTime: '06:00',
                deliveryEndTime: '18:00'
              },
              sameDay: data.preOrderDelivery?.sameDay || {
                cutoffTime: '06:00',
                deliveryStartTime: '06:00',
                deliveryEndTime: '18:00'
              },
              dualCutoff: data.preOrderDelivery?.dualCutoff || {
                morningEnabled: true,
                morningCutoffTime: '06:00',
                morningDeliveryStartTime: '06:00',
                morningDeliveryEndTime: '12:00',
                eveningEnabled: true,
                eveningCutoffTime: '12:00',
                eveningDeliveryStartTime: '17:00',
                eveningDeliveryEndTime: '21:00'
              }
            },
            orderAtCounter: {
              ...(data.orderAtCounter || data.inStore || {}),
              enabled: data.orderAtCounter?.enabled || data.inStore?.enabled || false,
              orderPrefix: data.orderAtCounter?.orderPrefix || 'OAC',
              orderCounter: data.orderAtCounter?.orderCounter || 1000,
              workflow: data.orderAtCounter?.workflow || data.inStore?.workflow || [
                { id: '1', label: 'Received', isFirst: true, isLast: false },
                { id: '2', label: 'In Progress', isFirst: false, isLast: false },
                { id: '3', label: 'Completed', isFirst: false, isLast: true }
              ]
            },
            orderAtTable: {
              ...(data.orderAtTable || {}),
              enabled: data.orderAtTable?.enabled || false,
              enablePayments: data.orderAtTable?.enablePayments !== undefined ? data.orderAtTable.enablePayments : true,
              enablePIN: data.orderAtTable?.enablePIN || false,
              enableBarcodeScanning: data.orderAtTable?.enableBarcodeScanning || false,
              orderPrefix: data.orderAtTable?.orderPrefix || 'OAT',
              orderCounter: data.orderAtTable?.orderCounter || 1000,
              pinType: data.orderAtTable?.pinType || 'daily',
              enableTip: data.orderAtTable?.enableTip !== undefined ? data.orderAtTable.enableTip : true,
              tipOptions: data.orderAtTable?.tipOptions || [10, 15, 20],
              workflow: data.orderAtTable?.workflow || [
                { id: '1', label: 'Received', isFirst: true, isLast: false },
                { id: '2', label: 'Preparing', isFirst: false, isLast: false },
                { id: '3', label: 'Ready', isFirst: false, isLast: false },
                { id: '4', label: 'Completed', isFirst: false, isLast: true }
              ],
              workflowPIN: data.orderAtTable?.workflowPIN || [
                { id: '1', label: 'Received', isFirst: true, isLast: false },
                { id: '2', label: 'Preparing', isFirst: false, isLast: false },
                { id: '3', label: 'Ready', isFirst: false, isLast: false },
                { id: '4', label: 'Awaiting Payment', isFirst: false, isLast: false },
                { id: '5', label: 'Completed', isFirst: false, isLast: true }
              ]
            },
            payAtTable: {
              ...(data.payAtTable || {}),
              enabled: data.payAtTable?.enabled || false,
              enableTableSelection: data.payAtTable?.enableTableSelection !== undefined ? data.payAtTable.enableTableSelection : true,
              enableBarcodeScanning: data.payAtTable?.enableBarcodeScanning || false
            },
            shipping: {
              ...(data.shipping || {}),
              enabled: data.shipping?.enabled || false,
              orderNumberPrefix: data.shipping?.orderNumberPrefix || 'SHIP',
              orderNumberStartFrom: data.shipping?.orderNumberStartFrom || 1000,
              workflow: data.shipping?.workflow || [
                { id: '1', label: 'Received', isFirst: true, isLast: false },
                { id: '2', label: 'Processing', isFirst: false, isLast: false },
                { id: '3', label: 'In Transit', isFirst: false, isLast: false },
                { id: '4', label: 'Delivered', isFirst: false, isLast: true }
              ],
              shippingType: 'free',
              flatShippingOptions: [
                { name: 'Regular Shipping', price: 5.00, description: 'Arrives in 5-7 business days' },
                { name: 'Priority Shipping', price: 10.00, description: 'Arrives in 3-5 business days' },
                { name: 'Express Shipping', price: 20.00, description: 'Arrives in 1-2 business days' }
              ],
              freeShippingDescription: 'Free shipping on all orders',
              categoryShipping: [],
              categoryShippingDescription: 'Shipping fee varies by product category',
              categoryDefaultType: 'free',
              categoryDefaultFee: 0,
              countries: data.shipping?.countries || [
                {
                  name: 'USA',
                  enabled: true,
                  states: [
                    { name: 'Alabama', enabled: true }, { name: 'Alaska', enabled: true }, { name: 'Arizona', enabled: true },
                    { name: 'Arkansas', enabled: true }, { name: 'California', enabled: true }, { name: 'Colorado', enabled: true },
                    { name: 'Connecticut', enabled: true }, { name: 'Delaware', enabled: true }, { name: 'Florida', enabled: true },
                    { name: 'Georgia', enabled: true }, { name: 'Hawaii', enabled: true }, { name: 'Idaho', enabled: true },
                    { name: 'Illinois', enabled: true }, { name: 'Indiana', enabled: true }, { name: 'Iowa', enabled: true },
                    { name: 'Kansas', enabled: true }, { name: 'Kentucky', enabled: true }, { name: 'Louisiana', enabled: true },
                    { name: 'Maine', enabled: true }, { name: 'Maryland', enabled: true }, { name: 'Massachusetts', enabled: true },
                    { name: 'Michigan', enabled: true }, { name: 'Minnesota', enabled: true }, { name: 'Mississippi', enabled: true },
                    { name: 'Missouri', enabled: true }, { name: 'Montana', enabled: true }, { name: 'Nebraska', enabled: true },
                    { name: 'Nevada', enabled: true }, { name: 'New Hampshire', enabled: true }, { name: 'New Jersey', enabled: true },
                    { name: 'New Mexico', enabled: true }, { name: 'New York', enabled: true }, { name: 'North Carolina', enabled: true },
                    { name: 'North Dakota', enabled: true }, { name: 'Ohio', enabled: true }, { name: 'Oklahoma', enabled: true },
                    { name: 'Oregon', enabled: true }, { name: 'Pennsylvania', enabled: true }, { name: 'Rhode Island', enabled: true },
                    { name: 'South Carolina', enabled: true }, { name: 'South Dakota', enabled: true }, { name: 'Tennessee', enabled: true },
                    { name: 'Texas', enabled: true }, { name: 'Utah', enabled: true }, { name: 'Vermont', enabled: true },
                    { name: 'Virginia', enabled: true }, { name: 'Washington', enabled: true }, { name: 'West Virginia', enabled: true },
                    { name: 'Wisconsin', enabled: true }, { name: 'Wyoming', enabled: true }
                  ]
                },
                {
                  name: 'India',
                  enabled: true,
                  states: [
                    { name: 'Andhra Pradesh', enabled: true }, { name: 'Arunachal Pradesh', enabled: true },
                    { name: 'Assam', enabled: true }, { name: 'Bihar', enabled: true }, { name: 'Chhattisgarh', enabled: true },
                    { name: 'Goa', enabled: true }, { name: 'Gujarat', enabled: true }, { name: 'Haryana', enabled: true },
                    { name: 'Himachal Pradesh', enabled: true }, { name: 'Jharkhand', enabled: true }, { name: 'Karnataka', enabled: true },
                    { name: 'Kerala', enabled: true }, { name: 'Madhya Pradesh', enabled: true }, { name: 'Maharashtra', enabled: true },
                    { name: 'Manipur', enabled: true }, { name: 'Meghalaya', enabled: true }, { name: 'Mizoram', enabled: true },
                    { name: 'Nagaland', enabled: true }, { name: 'Odisha', enabled: true }, { name: 'Punjab', enabled: true },
                    { name: 'Rajasthan', enabled: true }, { name: 'Sikkim', enabled: true }, { name: 'Tamil Nadu', enabled: true },
                    { name: 'Telangana', enabled: true }, { name: 'Tripura', enabled: true }, { name: 'Uttar Pradesh', enabled: true },
                    { name: 'Uttarakhand', enabled: true }, { name: 'West Bengal', enabled: true },
                    { name: 'Andaman and Nicobar Islands', enabled: true }, { name: 'Chandigarh', enabled: true },
                    { name: 'Dadra and Nagar Haveli and Daman and Diu', enabled: true }, { name: 'Delhi', enabled: true },
                    { name: 'Jammu and Kashmir', enabled: true }, { name: 'Ladakh', enabled: true }, { name: 'Lakshadweep', enabled: true },
                    { name: 'Puducherry', enabled: true }
                  ]
                }
              ]
            }
          };
          setSettings(updatedData);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Pickup location functions
  const openAddLocationModal = (tab) => {
    setCurrentLocationTab(tab);
    setEditingLocation(null);
    setLocationForm({ name: '', address: '', phone: '' });
    setShowAddLocationModal(true);
  };

  const openEditLocationModal = (tab, index) => {
    setCurrentLocationTab(tab);
    setEditingLocation(index);
    const locations = tab === 'orderNow' ? settings.orderNowPickup.pickupLocations : settings.preOrderPickup.pickupLocations;
    setLocationForm({ ...locations[index] });
    setShowAddLocationModal(true);
  };

  const handleSaveLocation = () => {
    if (!locationForm.name.trim() || !locationForm.address.trim()) {
      setMessage({ type: 'error', text: 'Name and Address are required' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    const isOrderNow = currentLocationTab === 'orderNow';
    const tabKey = isOrderNow ? 'orderNowPickup' : 'preOrderPickup';
    const currentLocations = settings[tabKey].pickupLocations || [];
    
    let updatedLocations;
    if (editingLocation !== null) {
      // Edit existing location
      updatedLocations = [...currentLocations];
      updatedLocations[editingLocation] = locationForm;
    } else {
      // Add new location
      updatedLocations = [...currentLocations, locationForm];
    }

    setSettings({
      ...settings,
      [tabKey]: {
        ...settings[tabKey],
        pickupLocations: updatedLocations
      }
    });
    
    setShowAddLocationModal(false);
    setLocationForm({ name: '', address: '', phone: '' });
    setCurrentLocationTab('');
    setMessage({ type: 'success', text: editingLocation !== null ? 'Location updated' : 'Location added' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleDeleteLocation = (tab, index) => {
    if (window.confirm('Are you sure you want to delete this pickup location?')) {
      const isOrderNow = tab === 'orderNow';
      const tabKey = isOrderNow ? 'orderNowPickup' : 'preOrderPickup';
      const currentLocations = settings[tabKey].pickupLocations || [];
      const updatedLocations = currentLocations.filter((_, i) => i !== index);
      
      setSettings({
        ...settings,
        [tabKey]: {
          ...settings[tabKey],
          pickupLocations: updatedLocations
        }
      });
      
      setMessage({ type: 'success', text: 'Location deleted' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  // Move workflow step up
  const moveWorkflowStepUp = (orderType, index) => {
    if (index === 0) return; // Can't move first step up
    
    const workflow = [...settings[orderType].workflow];
    const temp = workflow[index];
    workflow[index] = workflow[index - 1];
    workflow[index - 1] = temp;
    
    setSettings({
      ...settings,
      [orderType]: {
        ...settings[orderType],
        workflow: workflow
      }
    });
  };

  // Move workflow step down
  const moveWorkflowStepDown = (orderType, index) => {
    const workflow = [...settings[orderType].workflow];
    if (index === workflow.length - 1) return; // Can't move last step down
    
    const temp = workflow[index];
    workflow[index] = workflow[index + 1];
    workflow[index + 1] = temp;
    
    setSettings({
      ...settings,
      [orderType]: {
        ...settings[orderType],
        workflow: workflow
      }
    });
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ecommerce-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        showMessage('success', 'Settings saved successfully!');
      } else {
        showMessage('error', 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('error', 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // PIN Management Functions
  const fetchCurrentPIN = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/order-at-table/current-pin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentPIN(data);
      }
    } catch (error) {
      console.error('Error fetching PIN:', error);
    }
  };

  const generatePIN = async () => {
    setPinLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/order-at-table/generate-pin?pin_type=${settings.orderAtTable.pinType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentPIN({ pin: data.pin, type: settings.orderAtTable.pinType, expires_at: data.expires_at, expired: false });
        showMessage('success', 'PIN generated successfully!');
      } else {
        showMessage('error', 'Failed to generate PIN');
      }
    } catch (error) {
      console.error('Error generating PIN:', error);
      showMessage('error', 'Failed to generate PIN');
    } finally {
      setPinLoading(false);
    }
  };

  // Handle reset counter for an order type
  const handleResetCounter = (orderType, orderTypeName) => {
    setConfirmTitle('Reset Counter');
    setConfirmMessage(`Are you sure you want to reset the ${orderTypeName} order counter? This will start numbering from the configured starting number.`);
    setConfirmAction(() => async () => {
      setShowConfirmModal(false);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/orders/reset-counter`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            orderType: orderType,
            startNumber: settings[orderType].orderCounter
          })
        });
        
        if (response.ok) {
          showMessage('success', `${orderTypeName} counter reset successfully!`);
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Reset counter failed:', response.status, errorData);
          showMessage('error', `Failed to reset ${orderTypeName} counter: ${errorData.detail || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error resetting counter:', error);
        showMessage('error', `Failed to reset ${orderTypeName} counter`);
      }
    });
    setShowConfirmModal(true);
  };

  // Handle delete all orders for an order type
  const handleDeleteAllOrders = (orderType, orderTypeName) => {
    setConfirmTitle('Delete All Orders');
    setConfirmMessage(`⚠️ Are you sure you want to delete ALL ${orderTypeName} orders? This action CANNOT be undone!`);
    setConfirmAction(() => async () => {
      setShowConfirmModal(false);
      try {
        const token = localStorage.getItem('token');
        console.log('Attempting to delete orders...');
        console.log('Order Type:', orderType);
        console.log('Order Type Name:', orderTypeName);
        
        const requestBody = { orderType: orderType };
        console.log('Request body:', JSON.stringify(requestBody));
        
        const response = await fetch(`${API_BASE_URL}/api/orders/delete-by-type`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          },
          body: JSON.stringify(requestBody)
        });
        
        console.log('Delete response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Delete response data:', data);
          if (data.deletedCount === 0) {
            showMessage('info', `No ${orderTypeName} orders found to delete. Orders may be in a different order type.`);
          } else {
            showMessage('success', `Successfully deleted ${data.deletedCount} ${orderTypeName} orders`);
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Delete failed:', response.status, errorData);
          showMessage('error', `Failed to delete ${orderTypeName} orders: ${errorData.detail || response.statusText || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error deleting orders:', error);
        showMessage('error', `Failed to delete ${orderTypeName} orders: ${error.message}`);
      }
    });
    setShowConfirmModal(true);
  };

  const handleAddZipCode = () => {
    if (newZipCode.trim() && !newArea.zipCodes.includes(newZipCode.trim())) {
      setNewArea({
        ...newArea,
        zipCodes: [...newArea.zipCodes, newZipCode.trim()]
      });
      setNewZipCode('');
    }
  };

  const handleRemoveZipCode = (zipCode) => {
    setNewArea({
      ...newArea,
      zipCodes: newArea.zipCodes.filter(z => z !== zipCode)
    });
  };

  const handleSaveArea = () => {
    if (!newArea.cityName.trim() || newArea.zipCodes.length === 0) {
      showMessage('error', 'Please provide city name and at least one zip code');
      return;
    }

    const updatedSettings = { ...settings };
    const settingKey = activeTab === 'orderNowDelivery' ? 'orderNowDelivery' : 'preOrderDelivery';
    
    updatedSettings[settingKey].deliveryAreas = [
      ...updatedSettings[settingKey].deliveryAreas,
      { ...newArea }
    ];

    setSettings(updatedSettings);
    setNewArea({ cityName: '', zipCodes: [] });
    setShowAddAreaModal(false);
    showMessage('success', 'Delivery area added successfully!');
  };

  const handleRemoveArea = (settingKey, index) => {
    const updatedSettings = { ...settings };
    updatedSettings[settingKey].deliveryAreas.splice(index, 1);
    setSettings(updatedSettings);
  };

  const tabs = [
    { id: 'orderNowPickup', label: 'Order Now - Pickup', icon: Package },
    { id: 'orderNowDelivery', label: 'Order Now - Delivery', icon: Truck },
    { id: 'preOrderPickup', label: 'Pre Order - Pickup', icon: Calendar },
    { id: 'preOrderDelivery', label: 'Pre Order - Delivery', icon: Calendar },
    { id: 'orderAtCounter', label: 'Order at Counter', icon: Settings },
    { id: 'orderAtTable', label: 'Order at Table', icon: MapPin },
    { id: 'payAtTable', label: 'Pay at Table', icon: CreditCard },
    { id: 'shipping', label: 'Shipping', icon: Package }
  ];

  // Helper function to generate time options in 30-minute increments
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const time24 = `${hourStr}:${minuteStr}`;
        
        // Convert to 12-hour format
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const time12 = `${hour12}:${minuteStr} ${ampm}`;
        
        times.push({ value: time24, label: time12 });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">
                  Configure Order Types
                </h2>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-4 sm:p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'success' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex justify-between px-3 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => {
              const isEnabled = settings[tab.id]?.enabled;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 py-3 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[rgb(41,173,211)] text-[rgb(41,173,211)]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  {isEnabled && (
                    <svg className="h-3 w-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              );
            })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-4 sm:p-6">
          {/* Order Now - Pickup */}
          {activeTab === 'orderNowPickup' && (
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between py-4 border-b border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Enable immediate pickup orders
                  </label>
                  <p className="text-sm text-gray-500">
                    Allow customers to place orders for immediate pickup
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.orderNowPickup.enabled}
                    onChange={(e) => setSettings({
                      ...settings,
                      orderNowPickup: { ...settings.orderNowPickup, enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                </label>
              </div>

              {/* Order Number Configuration */}
              <div className="py-4 border-b border-gray-200">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-4">Order Number Configuration</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                      <input
                        type="text"
                        value={settings.orderNowPickup.orderPrefix || 'ONP'}
                        onChange={(e) => setSettings({ ...settings, orderNowPickup: { ...settings.orderNowPickup, orderPrefix: e.target.value }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="ONP"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                      <input
                        type="number"
                        value={settings.orderNowPickup.orderCounter || 1000}
                        onChange={(e) => setSettings({ ...settings, orderNowPickup: { ...settings.orderNowPickup, orderCounter: parseInt(e.target.value) || 1000 }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="1000"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleResetCounter('orderNowPickup', 'Order Now - Pickup')}
                      className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,163,201)]"
                    >
                      Reset Counter
                    </button>
                    <button
                      onClick={() => handleDeleteAllOrders('orderNowPickup', 'Order Now - Pickup')}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      Delete All Orders
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    Example: {settings.orderNowPickup.orderPrefix || 'ONP'}{settings.orderNowPickup.orderCounter || 1000}
                  </p>
                </div>
              </div>

              {/* Prep Time */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Regular Prep Time (minutes)
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Time needed to prepare regular orders
                </p>
                <input
                  type="number"
                  min="0"
                  placeholder="30"
                  value={settings.orderNowPickup.prepTime}
                  onChange={(e) => setSettings({
                    ...settings,
                    orderNowPickup: { ...settings.orderNowPickup, prepTime: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
              </div>

              {/* Pickup Locations for Order Now */}
              <div className="py-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900">
                      Pickup Locations
                    </label>
                    <p className="text-sm text-gray-500">
                      Add locations where customers can pick up their immediate orders
                    </p>
                  </div>
                  <button
                    onClick={() => openAddLocationModal('orderNow')}
                    className="flex items-center gap-2 px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-lg hover:bg-[rgb(31,163,201)] transition-colors text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Location
                  </button>
                </div>

                {/* Locations List */}
                {(!settings.orderNowPickup.pickupLocations || settings.orderNowPickup.pickupLocations.length === 0) ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No pickup locations added yet</p>
                    <p className="text-xs text-gray-400 mt-1">Click "Add Location" to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {settings.orderNowPickup.pickupLocations.map((location, index) => (
                      <div key={index} className="border border-gray-300 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="h-4 w-4 text-[rgb(41,173,211)]" />
                              <h4 className="font-medium text-gray-900 text-sm">{location.name}</h4>
                            </div>
                            <p className="text-sm text-gray-600 ml-6">{location.address}</p>
                            {location.phone && (
                              <p className="text-sm text-gray-500 ml-6 mt-1">{location.phone}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => openEditLocationModal('orderNow', index)}
                              className="p-2 text-gray-600 hover:text-[rgb(41,173,211)] hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit location"
                            >
                              <Settings className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLocation('orderNow', index)}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete location"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Workflow Section */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Workflow Steps</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentWorkflowTab('orderNowPickup');
                      setEditingWorkflowStepIndex(null);
                      setWorkflowStepLabel('');
                      setShowAddWorkflowStepModal(true);
                    }}
                    className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Step
                  </button>
                </div>

                {/* Workflow Steps List */}
                <div className="space-y-3">
                  {settings.orderNowPickup.workflow && settings.orderNowPickup.workflow.length > 0 ? (
                    settings.orderNowPickup.workflow.map((step, index) => (
                      <div key={step.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-6 h-6 bg-[rgb(41,173,211)] text-white rounded-full text-xs font-semibold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{step.label}</span>
                              {step.isFirst && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">First Step</span>
                              )}
                              {step.isLast && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Last Step</span>
                              )}
                              {step.isPaymentStep && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  Payment Step
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Move Up Button - disabled for first step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepUp('orderNowPickup', index)}
                            disabled={index === 0}
                            className={`p-2 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          {/* Move Down Button - disabled for last step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepDown('orderNowPickup', index)}
                            disabled={index === settings.orderNowPickup.workflow.length - 1}
                            className={`p-2 rounded ${index === settings.orderNowPickup.workflow.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentWorkflowTab('orderNowPickup');
                              setEditingWorkflowStepIndex(index);
                              setWorkflowStepLabel(step.label);
                              setIsPaymentStep(step.isPaymentStep || false);
                              setIsPaymentStep(step.isPaymentStep || false);
                              setShowAddWorkflowStepModal(true);
                            }}
                            className="p-2 text-[rgb(41,173,211)] hover:bg-[rgba(41,173,211,0.1)] rounded"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedWorkflow = settings.orderNowPickup.workflow.filter((_, i) => i !== index);
                              setSettings({
                                ...settings,
                                orderNowPickup: {
                                  ...settings.orderNowPickup,
                                  workflow: updatedWorkflow
                                }
                              });
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                            disabled={settings.orderNowPickup.workflow.length <= 1}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No workflow steps defined</p>
                  )}
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> The first step is automatically assigned when an order is created. The last step marks the order as complete.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Order Now - Delivery */}
          {activeTab === 'orderNowDelivery' && (
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between py-4 border-b border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Enable immediate delivery orders
                  </label>
                  <p className="text-sm text-gray-500">
                    Allow customers to place orders for immediate delivery
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.orderNowDelivery.enabled}
                    onChange={(e) => setSettings({
                      ...settings,
                      orderNowDelivery: { ...settings.orderNowDelivery, enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                </label>
              </div>

              {/* Order Number Configuration */}
              <div className="py-4 border-b border-gray-200">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-4">Order Number Configuration</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                      <input
                        type="text"
                        value={settings.orderNowDelivery.orderPrefix || 'OND'}
                        onChange={(e) => setSettings({ ...settings, orderNowDelivery: { ...settings.orderNowDelivery, orderPrefix: e.target.value }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="OND"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                      <input
                        type="number"
                        value={settings.orderNowDelivery.orderCounter || 1000}
                        onChange={(e) => setSettings({ ...settings, orderNowDelivery: { ...settings.orderNowDelivery, orderCounter: parseInt(e.target.value) || 1000 }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="1000"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleResetCounter('orderNowDelivery', 'Order Now - Delivery')}
                      className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,163,201)]"
                    >
                      Reset Counter
                    </button>
                    <button
                      onClick={() => handleDeleteAllOrders('orderNowDelivery', 'Order Now - Delivery')}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      Delete All Orders
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    Example: {settings.orderNowDelivery.orderPrefix || 'OND'}{settings.orderNowDelivery.orderCounter || 1000}
                  </p>
                </div>
              </div>

              {/* Prep Time */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Prep Time (minutes)
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Time to prepare the order
                </p>
                <input
                  type="number"
                  min="0"
                  placeholder="30"
                  value={settings.orderNowDelivery.prepTime}
                  onChange={(e) => setSettings({
                    ...settings,
                    orderNowDelivery: { ...settings.orderNowDelivery, prepTime: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
              </div>

              {/* Delivery Time */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Delivery Time (minutes)
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Time to deliver the order
                </p>
                <input
                  type="number"
                  min="0"
                  placeholder="30"
                  value={settings.orderNowDelivery.deliveryTime}
                  onChange={(e) => setSettings({
                    ...settings,
                    orderNowDelivery: { ...settings.orderNowDelivery, deliveryTime: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
              </div>

              {/* Delivery Fee */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-4">
                  Delivery Fee
                </label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryFee"
                      checked={settings.orderNowDelivery.deliveryFeeType === 'free'}
                      onChange={() => setSettings({
                        ...settings,
                        orderNowDelivery: { ...settings.orderNowDelivery, deliveryFeeType: 'free', deliveryFee: 0 }
                      })}
                      className="h-4 w-4 text-[rgb(41,173,211)]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Free Delivery</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryFee"
                      checked={settings.orderNowDelivery.deliveryFeeType === 'charge'}
                      onChange={() => setSettings({
                        ...settings,
                        orderNowDelivery: { ...settings.orderNowDelivery, deliveryFeeType: 'charge' }
                      })}
                      className="h-4 w-4 text-[rgb(41,173,211)]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Charge Delivery Fee</span>
                  </label>
                  {settings.orderNowDelivery.deliveryFeeType === 'charge' && (
                    <div className="ml-6 mt-2">
                      <div className="relative w-full max-w-xs">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={settings.orderNowDelivery.deliveryFee}
                          onChange={(e) => setSettings({
                            ...settings,
                            orderNowDelivery: { ...settings.orderNowDelivery, deliveryFee: parseFloat(e.target.value) || 0 }
                          })}
                          className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Areas */}
              <div className="py-4">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-900">
                    Delivery Areas
                  </label>
                  <button
                    onClick={() => setShowAddAreaModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]"
                  >
                    <Plus className="h-4 w-4" />
                    Add Area
                  </button>
                </div>

                {settings.orderNowDelivery.deliveryAreas.length === 0 ? (
                  <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
                    No delivery areas configured. Add at least one area to enable delivery.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {settings.orderNowDelivery.deliveryAreas.map((area, index) => (
                      <div key={index} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">{area.cityName}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Zip Codes: {area.zipCodes.join(', ')}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveArea('orderNowDelivery', index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Workflow Section */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Workflow Steps</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentWorkflowTab('orderNowDelivery');
                      setEditingWorkflowStepIndex(null);
                      setWorkflowStepLabel('');
                      setShowAddWorkflowStepModal(true);
                    }}
                    className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Step
                  </button>
                </div>

                <div className="space-y-3">
                  {settings.orderNowDelivery.workflow && settings.orderNowDelivery.workflow.length > 0 ? (
                    settings.orderNowDelivery.workflow.map((step, index) => (
                      <div key={step.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-6 h-6 bg-[rgb(41,173,211)] text-white rounded-full text-xs font-semibold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{step.label}</span>
                              {step.isFirst && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">First Step</span>
                              )}
                              {step.isLast && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Last Step</span>
                              )}
                              {step.isPaymentStep && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  Payment Step
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Move Up Button - disabled for first step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepUp('orderNowDelivery', index)}
                            disabled={index === 0}
                            className={`p-2 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          {/* Move Down Button - disabled for last step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepDown('orderNowDelivery', index)}
                            disabled={index === settings.orderNowDelivery.workflow.length - 1}
                            className={`p-2 rounded ${index === settings.orderNowDelivery.workflow.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentWorkflowTab('orderNowDelivery');
                              setEditingWorkflowStepIndex(index);
                              setWorkflowStepLabel(step.label);
                              setIsPaymentStep(step.isPaymentStep || false);
                              setIsPaymentStep(step.isPaymentStep || false);
                              setShowAddWorkflowStepModal(true);
                            }}
                            className="p-2 text-[rgb(41,173,211)] hover:bg-[rgba(41,173,211,0.1)] rounded"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedWorkflow = settings.orderNowDelivery.workflow.filter((_, i) => i !== index);
                              setSettings({
                                ...settings,
                                orderNowDelivery: {
                                  ...settings.orderNowDelivery,
                                  workflow: updatedWorkflow
                                }
                              });
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                            disabled={settings.orderNowDelivery.workflow.length <= 1}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No workflow steps defined</p>
                  )}
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> The first step is automatically assigned when an order is created. The last step marks the order as complete.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pre Order - Pickup */}
          {activeTab === 'preOrderPickup' && (
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between py-4 border-b border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Allow customers to pick up pre-orders
                  </label>
                  <p className="text-sm text-gray-500">
                    Enable pre-order pickup functionality
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.preOrderPickup.enabled}
                    onChange={(e) => setSettings({
                      ...settings,
                      preOrderPickup: { ...settings.preOrderPickup, enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                </label>
              </div>

              {/* Order Number Configuration */}
              <div className="py-4 border-b border-gray-200">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-4">Order Number Configuration</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                      <input
                        type="text"
                        value={settings.preOrderPickup.orderPrefix || 'POP'}
                        onChange={(e) => setSettings({ ...settings, preOrderPickup: { ...settings.preOrderPickup, orderPrefix: e.target.value }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="POP"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                      <input
                        type="number"
                        value={settings.preOrderPickup.orderCounter || 1000}
                        onChange={(e) => setSettings({ ...settings, preOrderPickup: { ...settings.preOrderPickup, orderCounter: parseInt(e.target.value) || 1000 }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="1000"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleResetCounter('preOrderPickup', 'Pre-Order - Pickup')}
                      className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,163,201)]"
                    >
                      Reset Counter
                    </button>
                    <button
                      onClick={() => handleDeleteAllOrders('preOrderPickup', 'Pre-Order - Pickup')}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      Delete All Orders
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    Example: {settings.preOrderPickup.orderPrefix || 'POP'}{settings.preOrderPickup.orderCounter || 1000}
                  </p>
                </div>
              </div>

              {/* Pre-Order Pickup Options */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-4">
                  Pre-Order Pickup Options
                </label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="pickupOption"
                      checked={settings.preOrderPickup.pickupOption === 'oneDayBefore'}
                      onChange={() => setSettings({
                        ...settings,
                        preOrderPickup: { ...settings.preOrderPickup, pickupOption: 'oneDayBefore' }
                      })}
                      className="h-4 w-4 text-[rgb(41,173,211)]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Preorder with 1 day before cutoff</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="pickupOption"
                      checked={settings.preOrderPickup.pickupOption === 'sameDay'}
                      onChange={() => setSettings({
                        ...settings,
                        preOrderPickup: { ...settings.preOrderPickup, pickupOption: 'sameDay' }
                      })}
                      className="h-4 w-4 text-[rgb(41,173,211)]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Preorder with same day cutoff</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="pickupOption"
                      checked={settings.preOrderPickup.pickupOption === 'dualCutoff'}
                      onChange={() => setSettings({
                        ...settings,
                        preOrderPickup: { ...settings.preOrderPickup, pickupOption: 'dualCutoff' }
                      })}
                      className="h-4 w-4 text-[rgb(41,173,211)]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Same Day Preorder with dual cutoff times (morning & evening)</span>
                  </label>
                </div>
              </div>

              {/* Conditional Time Settings based on selected option */}
              {settings.preOrderPickup.pickupOption === 'oneDayBefore' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Preorder with 1 day before cutoff</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Cutoff Time</label>
                      <select
                        value={settings.preOrderPickup.oneDayBefore.cutoffTime}
                        onChange={(e) => setSettings({
                          ...settings,
                          preOrderPickup: {
                            ...settings.preOrderPickup,
                            oneDayBefore: { ...settings.preOrderPickup.oneDayBefore, cutoffTime: e.target.value }
                          }
                        })}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                      >
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Pickup Start Time</label>
                      <select
                        value={settings.preOrderPickup.oneDayBefore.pickupStartTime}
                        onChange={(e) => setSettings({
                          ...settings,
                          preOrderPickup: {
                            ...settings.preOrderPickup,
                            oneDayBefore: { ...settings.preOrderPickup.oneDayBefore, pickupStartTime: e.target.value }
                          }
                        })}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                      >
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Pickup End Time</label>
                      <select
                        value={settings.preOrderPickup.oneDayBefore.pickupEndTime}
                        onChange={(e) => setSettings({
                          ...settings,
                          preOrderPickup: {
                            ...settings.preOrderPickup,
                            oneDayBefore: { ...settings.preOrderPickup.oneDayBefore, pickupEndTime: e.target.value }
                          }
                        })}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                      >
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {settings.preOrderPickup.pickupOption === 'sameDay' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Preorder with same day cutoff</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Cutoff Time</label>
                      <select
                        value={settings.preOrderPickup.sameDay.cutoffTime}
                        onChange={(e) => setSettings({
                          ...settings,
                          preOrderPickup: {
                            ...settings.preOrderPickup,
                            sameDay: { ...settings.preOrderPickup.sameDay, cutoffTime: e.target.value }
                          }
                        })}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                      >
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Pickup Start Time</label>
                      <select
                        value={settings.preOrderPickup.sameDay.pickupStartTime}
                        onChange={(e) => setSettings({
                          ...settings,
                          preOrderPickup: {
                            ...settings.preOrderPickup,
                            sameDay: { ...settings.preOrderPickup.sameDay, pickupStartTime: e.target.value }
                          }
                        })}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                      >
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Pickup End Time</label>
                      <select
                        value={settings.preOrderPickup.sameDay.pickupEndTime}
                        onChange={(e) => setSettings({
                          ...settings,
                          preOrderPickup: {
                            ...settings.preOrderPickup,
                            sameDay: { ...settings.preOrderPickup.sameDay, pickupEndTime: e.target.value }
                          }
                        })}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                      >
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {settings.preOrderPickup.pickupOption === 'dualCutoff' && (
                <div className="py-4 space-y-6">
                  <h3 className="text-sm font-semibold text-gray-900">Same Day Preorder with dual cutoff times (morning & evening)</h3>
                  
                  {/* Morning Pickup */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-900">
                        Enable Morning Pickup
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.preOrderPickup.dualCutoff.morningEnabled}
                          onChange={(e) => setSettings({
                            ...settings,
                            preOrderPickup: {
                              ...settings.preOrderPickup,
                              dualCutoff: { ...settings.preOrderPickup.dualCutoff, morningEnabled: e.target.checked }
                            }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                      </label>
                    </div>
                    {settings.preOrderPickup.dualCutoff.morningEnabled && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Cutoff Time</label>
                          <select
                            value={settings.preOrderPickup.dualCutoff.morningCutoffTime}
                            onChange={(e) => setSettings({
                              ...settings,
                              preOrderPickup: {
                                ...settings.preOrderPickup,
                                dualCutoff: { ...settings.preOrderPickup.dualCutoff, morningCutoffTime: e.target.value }
                              }
                            })}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          >
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Pickup Start Time</label>
                          <select
                            value={settings.preOrderPickup.dualCutoff.morningPickupStartTime}
                            onChange={(e) => setSettings({
                              ...settings,
                              preOrderPickup: {
                                ...settings.preOrderPickup,
                                dualCutoff: { ...settings.preOrderPickup.dualCutoff, morningPickupStartTime: e.target.value }
                              }
                            })}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          >
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Pickup End Time</label>
                          <select
                            value={settings.preOrderPickup.dualCutoff.morningPickupEndTime}
                            onChange={(e) => setSettings({
                              ...settings,
                              preOrderPickup: {
                                ...settings.preOrderPickup,
                                dualCutoff: { ...settings.preOrderPickup.dualCutoff, morningPickupEndTime: e.target.value }
                              }
                            })}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          >
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Evening Pickup */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-900">
                        Enable Evening Pickup
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.preOrderPickup.dualCutoff.eveningEnabled}
                          onChange={(e) => setSettings({
                            ...settings,
                            preOrderPickup: {
                              ...settings.preOrderPickup,
                              dualCutoff: { ...settings.preOrderPickup.dualCutoff, eveningEnabled: e.target.checked }
                            }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                      </label>
                    </div>
                    {settings.preOrderPickup.dualCutoff.eveningEnabled && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Cutoff Time</label>
                          <select
                            value={settings.preOrderPickup.dualCutoff.eveningCutoffTime}
                            onChange={(e) => setSettings({
                              ...settings,
                              preOrderPickup: {
                                ...settings.preOrderPickup,
                                dualCutoff: { ...settings.preOrderPickup.dualCutoff, eveningCutoffTime: e.target.value }
                              }
                            })}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          >
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Pickup Start Time</label>
                          <select
                            value={settings.preOrderPickup.dualCutoff.eveningPickupStartTime}
                            onChange={(e) => setSettings({
                              ...settings,
                              preOrderPickup: {
                                ...settings.preOrderPickup,
                                dualCutoff: { ...settings.preOrderPickup.dualCutoff, eveningPickupStartTime: e.target.value }
                              }
                            })}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          >
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Pickup End Time</label>
                          <select
                            value={settings.preOrderPickup.dualCutoff.eveningPickupEndTime}
                            onChange={(e) => setSettings({
                              ...settings,
                              preOrderPickup: {
                                ...settings.preOrderPickup,
                                dualCutoff: { ...settings.preOrderPickup.dualCutoff, eveningPickupEndTime: e.target.value }
                              }
                            })}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          >
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Catering Preparation Time - MOVED AFTER TIME SETTINGS */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Catering Preparation Time (hours)
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Additional preparation time required for catering items (default: 24 hours)
                </p>
                <input
                  type="number"
                  min="0"
                  placeholder="24"
                  value={settings.preOrderPickup.cateringPrepTime}
                  onChange={(e) => setSettings({
                    ...settings,
                    preOrderPickup: { ...settings.preOrderPickup, cateringPrepTime: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
              </div>

              {/* Pre-Order Discount - MOVED AFTER TIME SETTINGS */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Discount Percentage for Pre-Orders (%)
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Offer a discount to encourage customers to use pre-order options. Leave at 0 to disable discount.
                </p>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="0"
                  value={settings.preOrderPickup.discountPercentage}
                  onChange={(e) => setSettings({
                    ...settings,
                    preOrderPickup: { ...settings.preOrderPickup, discountPercentage: parseFloat(e.target.value) || 0 }
                  })}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
              </div>

              {/* Pickup Locations for Pre-Order */}
              <div className="py-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900">
                      Pickup Locations
                    </label>
                    <p className="text-sm text-gray-500">
                      Add locations where customers can pick up their pre-orders
                    </p>
                  </div>
                  <button
                    onClick={() => openAddLocationModal('preOrder')}
                    className="flex items-center gap-2 px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-lg hover:bg-[rgb(31,163,201)] transition-colors text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Location
                  </button>
                </div>

                {/* Locations List */}
                {(!settings.preOrderPickup.pickupLocations || settings.preOrderPickup.pickupLocations.length === 0) ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No pickup locations added yet</p>
                    <p className="text-xs text-gray-400 mt-1">Click "Add Location" to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {settings.preOrderPickup.pickupLocations.map((location, index) => (
                      <div key={index} className="border border-gray-300 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="h-4 w-4 text-[rgb(41,173,211)]" />
                              <h4 className="font-medium text-gray-900 text-sm">{location.name}</h4>
                            </div>
                            <p className="text-sm text-gray-600 ml-6">{location.address}</p>
                            {location.phone && (
                              <p className="text-sm text-gray-500 ml-6 mt-1">{location.phone}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => openEditLocationModal('preOrder', index)}
                              className="p-2 text-gray-600 hover:text-[rgb(41,173,211)] hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit location"
                            >
                              <Settings className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLocation('preOrder', index)}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete location"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Workflow Section for Pre-Order Pickup */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Workflow Steps</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentWorkflowTab('preOrderPickup');
                      setEditingWorkflowStepIndex(null);
                      setWorkflowStepLabel('');
                      setShowAddWorkflowStepModal(true);
                    }}
                    className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Step
                  </button>
                </div>

                <div className="space-y-3">
                  {settings.preOrderPickup.workflow && settings.preOrderPickup.workflow.length > 0 ? (
                    settings.preOrderPickup.workflow.map((step, index) => (
                      <div key={step.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-6 h-6 bg-[rgb(41,173,211)] text-white rounded-full text-xs font-semibold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{step.label}</span>
                              {step.isFirst && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">First Step</span>
                              )}
                              {step.isLast && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Last Step</span>
                              )}
                              {step.isPaymentStep && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  Payment Step
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Move Up Button - disabled for first step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepUp('preOrderPickup', index)}
                            disabled={index === 0}
                            className={`p-2 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          {/* Move Down Button - disabled for last step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepDown('preOrderPickup', index)}
                            disabled={index === settings.preOrderPickup.workflow.length - 1}
                            className={`p-2 rounded ${index === settings.preOrderPickup.workflow.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentWorkflowTab('preOrderPickup');
                              setEditingWorkflowStepIndex(index);
                              setWorkflowStepLabel(step.label);
                              setIsPaymentStep(step.isPaymentStep || false);
                              setShowAddWorkflowStepModal(true);
                            }}
                            className="p-2 text-[rgb(41,173,211)] hover:bg-[rgba(41,173,211,0.1)] rounded"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedWorkflow = settings.preOrderPickup.workflow.filter((_, i) => i !== index);
                              setSettings({
                                ...settings,
                                preOrderPickup: {
                                  ...settings.preOrderPickup,
                                  workflow: updatedWorkflow
                                }
                              });
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                            disabled={settings.preOrderPickup.workflow.length <= 1}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No workflow steps defined</p>
                  )}
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> The first step is automatically assigned when an order is created. The last step marks the order as complete.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pre Order - Delivery */}
          {activeTab === 'preOrderDelivery' && (
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between py-4 border-b border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Allow customers to order for pre-order delivery
                  </label>
                  <p className="text-sm text-gray-500">
                    Enable pre-order delivery functionality
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.preOrderDelivery.enabled}
                    onChange={(e) => setSettings({
                      ...settings,
                      preOrderDelivery: { ...settings.preOrderDelivery, enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                </label>
              </div>

              {/* Order Number Configuration */}
              <div className="py-4 border-b border-gray-200">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-4">Order Number Configuration</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                      <input
                        type="text"
                        value={settings.preOrderDelivery.orderPrefix || 'POD'}
                        onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, orderPrefix: e.target.value }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="POD"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                      <input
                        type="number"
                        value={settings.preOrderDelivery.orderCounter || 1000}
                        onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, orderCounter: parseInt(e.target.value) || 1000 }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="1000"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleResetCounter('preOrderDelivery', 'Pre-Order - Delivery')}
                      className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,163,201)]"
                    >
                      Reset Counter
                    </button>
                    <button
                      onClick={() => handleDeleteAllOrders('preOrderDelivery', 'Pre-Order - Delivery')}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      Delete All Orders
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    Example: {settings.preOrderDelivery.orderPrefix || 'POD'}{settings.preOrderDelivery.orderCounter || 1000}
                  </p>
                </div>
              </div>

              {/* Pre-Order Delivery Options */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-4">
                  Pre-Order Delivery Options
                </label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryOption"
                      checked={settings.preOrderDelivery.deliveryOption === 'oneDayBefore'}
                      onChange={() => setSettings({
                        ...settings,
                        preOrderDelivery: { ...settings.preOrderDelivery, deliveryOption: 'oneDayBefore' }
                      })}
                      className="h-4 w-4 text-[rgb(41,173,211)]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Preorder with 1 day before cutoff</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryOption"
                      checked={settings.preOrderDelivery.deliveryOption === 'sameDay'}
                      onChange={() => setSettings({
                        ...settings,
                        preOrderDelivery: { ...settings.preOrderDelivery, deliveryOption: 'sameDay' }
                      })}
                      className="h-4 w-4 text-[rgb(41,173,211)]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Preorder with same day cutoff</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryOption"
                      checked={settings.preOrderDelivery.deliveryOption === 'dualCutoff'}
                      onChange={() => setSettings({
                        ...settings,
                        preOrderDelivery: { ...settings.preOrderDelivery, deliveryOption: 'dualCutoff' }
                      })}
                      className="h-4 w-4 text-[rgb(41,173,211)]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Same Day Preorder with dual cutoff times (morning & evening)</span>
                  </label>
                </div>
              </div>

              {/* Conditional Time Settings - RIGHT AFTER RADIO BUTTONS */}
              {settings.preOrderDelivery.deliveryOption === 'oneDayBefore' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Preorder with 1 day before cutoff</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Cutoff Time</label>
                      <select value={settings.preOrderDelivery.oneDayBefore.cutoffTime}
                        onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, oneDayBefore: { ...settings.preOrderDelivery.oneDayBefore, cutoffTime: e.target.value }}})}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Delivery Start Time</label>
                      <select value={settings.preOrderDelivery.oneDayBefore.deliveryStartTime}
                        onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, oneDayBefore: { ...settings.preOrderDelivery.oneDayBefore, deliveryStartTime: e.target.value }}})}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Delivery End Time</label>
                      <select value={settings.preOrderDelivery.oneDayBefore.deliveryEndTime}
                        onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, oneDayBefore: { ...settings.preOrderDelivery.oneDayBefore, deliveryEndTime: e.target.value }}})}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {settings.preOrderDelivery.deliveryOption === 'sameDay' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Preorder with same day cutoff</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Cutoff Time</label>
                      <select value={settings.preOrderDelivery.sameDay.cutoffTime}
                        onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, sameDay: { ...settings.preOrderDelivery.sameDay, cutoffTime: e.target.value }}})}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Delivery Start Time</label>
                      <select value={settings.preOrderDelivery.sameDay.deliveryStartTime}
                        onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, sameDay: { ...settings.preOrderDelivery.sameDay, deliveryStartTime: e.target.value }}})}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Delivery End Time</label>
                      <select value={settings.preOrderDelivery.sameDay.deliveryEndTime}
                        onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, sameDay: { ...settings.preOrderDelivery.sameDay, deliveryEndTime: e.target.value }}})}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                        {timeOptions.map(time => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {settings.preOrderDelivery.deliveryOption === 'dualCutoff' && (
                <div className="py-4 border-b border-gray-200 space-y-6">
                  <h3 className="text-sm font-semibold text-gray-900">Same Day Preorder with dual cutoff times (morning & evening)</h3>
                  
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-900">Enable Morning Delivery</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={settings.preOrderDelivery.dualCutoff.morningEnabled}
                          onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, dualCutoff: { ...settings.preOrderDelivery.dualCutoff, morningEnabled: e.target.checked }}})}
                          className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                      </label>
                    </div>
                    {settings.preOrderDelivery.dualCutoff.morningEnabled && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Cutoff Time</label>
                          <select value={settings.preOrderDelivery.dualCutoff.morningCutoffTime}
                            onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, dualCutoff: { ...settings.preOrderDelivery.dualCutoff, morningCutoffTime: e.target.value }}})}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Delivery Start Time</label>
                          <select value={settings.preOrderDelivery.dualCutoff.morningDeliveryStartTime}
                            onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, dualCutoff: { ...settings.preOrderDelivery.dualCutoff, morningDeliveryStartTime: e.target.value }}})}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Delivery End Time</label>
                          <select value={settings.preOrderDelivery.dualCutoff.morningDeliveryEndTime}
                            onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, dualCutoff: { ...settings.preOrderDelivery.dualCutoff, morningDeliveryEndTime: e.target.value }}})}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-900">Enable Evening Delivery</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={settings.preOrderDelivery.dualCutoff.eveningEnabled}
                          onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, dualCutoff: { ...settings.preOrderDelivery.dualCutoff, eveningEnabled: e.target.checked }}})}
                          className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                      </label>
                    </div>
                    {settings.preOrderDelivery.dualCutoff.eveningEnabled && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Cutoff Time</label>
                          <select value={settings.preOrderDelivery.dualCutoff.eveningCutoffTime}
                            onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, dualCutoff: { ...settings.preOrderDelivery.dualCutoff, eveningCutoffTime: e.target.value }}})}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Delivery Start Time</label>
                          <select value={settings.preOrderDelivery.dualCutoff.eveningDeliveryStartTime}
                            onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, dualCutoff: { ...settings.preOrderDelivery.dualCutoff, eveningDeliveryStartTime: e.target.value }}})}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-900 mb-2">Delivery End Time</label>
                          <select value={settings.preOrderDelivery.dualCutoff.eveningDeliveryEndTime}
                            onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, dualCutoff: { ...settings.preOrderDelivery.dualCutoff, eveningDeliveryEndTime: e.target.value }}})}
                            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm">
                            {timeOptions.map(time => (
                              <option key={time.value} value={time.value}>{time.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Delivery Fee */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-4">Delivery Fee</label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input type="radio" name="preDeliveryFee" checked={settings.preOrderDelivery.deliveryFeeType === 'free'}
                      onChange={() => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, deliveryFeeType: 'free', deliveryFee: 0 }})}
                      className="h-4 w-4 text-[rgb(41,173,211)]" />
                    <span className="ml-2 text-sm text-gray-700">Free Delivery</span>
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="preDeliveryFee" checked={settings.preOrderDelivery.deliveryFeeType === 'charge'}
                      onChange={() => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, deliveryFeeType: 'charge' }})}
                      className="h-4 w-4 text-[rgb(41,173,211)]" />
                    <span className="ml-2 text-sm text-gray-700">Charge Delivery Fee</span>
                  </label>
                  {settings.preOrderDelivery.deliveryFeeType === 'charge' && (
                    <div className="ml-6 mt-2">
                      <div className="relative w-full max-w-xs">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                        <input type="number" min="0" step="0.01" placeholder="0.00" value={settings.preOrderDelivery.deliveryFee}
                          onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, deliveryFee: parseFloat(e.target.value) || 0 }})}
                          className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Areas */}
              <div className="py-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-900">Delivery Areas</label>
                  <button type="button" onClick={() => setShowAddAreaModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]">
                    <Plus className="h-4 w-4" />Add Area
                  </button>
                </div>
                {settings.preOrderDelivery.deliveryAreas.length === 0 ? (
                  <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">No delivery areas configured. Add at least one area to enable delivery.</div>
                ) : (
                  <div className="space-y-3">
                    {settings.preOrderDelivery.deliveryAreas.map((area, index) => (
                      <div key={index} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">{area.cityName}</h4>
                          <p className="text-sm text-gray-600 mt-1">Zip Codes: {area.zipCodes.join(', ')}</p>
                        </div>
                        <button type="button" onClick={() => handleRemoveArea('preOrderDelivery', index)} className="text-red-600 hover:text-red-800">
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Catering Preparation Time */}
              <div className="py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-900 mb-2">Catering Preparation Time (hours)</label>
                <p className="text-sm text-gray-500 mb-3">Additional preparation time required for catering items (default: 24 hours)</p>
                <input type="number" min="0" placeholder="24" value={settings.preOrderDelivery.cateringPrepTime}
                  onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, cateringPrepTime: parseInt(e.target.value) || 0 }})}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm" />
              </div>

              {/* Pre-Order Discount */}
              <div className="py-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">Discount Percentage for Pre-Orders (%)</label>
                <p className="text-sm text-gray-500 mb-3">Offer a discount to encourage customers to use pre-order options. Leave at 0 to disable discount.</p>
                <input type="number" min="0" max="100" step="0.1" placeholder="0" value={settings.preOrderDelivery.discountPercentage}
                  onChange={(e) => setSettings({ ...settings, preOrderDelivery: { ...settings.preOrderDelivery, discountPercentage: parseFloat(e.target.value) || 0 }})}
                  className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm" />
              </div>

              {/* Workflow Section for Pre-Order Delivery */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Workflow Steps</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentWorkflowTab('preOrderDelivery');
                      setEditingWorkflowStepIndex(null);
                      setWorkflowStepLabel('');
                      setShowAddWorkflowStepModal(true);
                    }}
                    className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Step
                  </button>
                </div>

                <div className="space-y-3">
                  {settings.preOrderDelivery.workflow && settings.preOrderDelivery.workflow.length > 0 ? (
                    settings.preOrderDelivery.workflow.map((step, index) => (
                      <div key={step.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-6 h-6 bg-[rgb(41,173,211)] text-white rounded-full text-xs font-semibold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{step.label}</span>
                              {step.isFirst && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">First Step</span>
                              )}
                              {step.isLast && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Last Step</span>
                              )}
                              {step.isPaymentStep && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  Payment Step
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Move Up Button - disabled for first step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepUp('preOrderDelivery', index)}
                            disabled={index === 0}
                            className={`p-2 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          {/* Move Down Button - disabled for last step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepDown('preOrderDelivery', index)}
                            disabled={index === settings.preOrderDelivery.workflow.length - 1}
                            className={`p-2 rounded ${index === settings.preOrderDelivery.workflow.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentWorkflowTab('preOrderDelivery');
                              setEditingWorkflowStepIndex(index);
                              setWorkflowStepLabel(step.label);
                              setIsPaymentStep(step.isPaymentStep || false);
                              setShowAddWorkflowStepModal(true);
                            }}
                            className="p-2 text-[rgb(41,173,211)] hover:bg-[rgba(41,173,211,0.1)] rounded"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedWorkflow = settings.preOrderDelivery.workflow.filter((_, i) => i !== index);
                              setSettings({
                                ...settings,
                                preOrderDelivery: {
                                  ...settings.preOrderDelivery,
                                  workflow: updatedWorkflow
                                }
                              });
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                            disabled={settings.preOrderDelivery.workflow.length <= 1}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No workflow steps defined</p>
                  )}
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> The first step is automatically assigned when an order is created. The last step marks the order as complete.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Order at Counter */}
          {activeTab === 'orderAtCounter' && (
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Enable Order at Counter</h3>
                  <p className="text-sm text-gray-500 mt-1">Allow customers to order at counter (pay now or later)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={settings.orderAtCounter.enabled}
                    onChange={(e) => setSettings({ ...settings, orderAtCounter: { ...settings.orderAtCounter, enabled: e.target.checked }})}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                </label>
              </div>

              {/* Order Number Configuration */}
              <div className="py-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-4">Order Number Configuration</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                      <input
                        type="text"
                        value={settings.orderAtCounter.orderPrefix || 'INS'}
                        onChange={(e) => setSettings({ ...settings, orderAtCounter: { ...settings.orderAtCounter, orderPrefix: e.target.value }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="INS"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                      <input
                        type="number"
                        value={settings.orderAtCounter.orderCounter || 1000}
                        onChange={(e) => setSettings({ ...settings, orderAtCounter: { ...settings.orderAtCounter, orderCounter: parseInt(e.target.value) || 1000 }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="1000"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleResetCounter('orderAtCounter', 'Order at Counter')}
                      className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,163,201)]"
                    >
                      Reset Counter
                    </button>
                    <button
                      onClick={() => handleDeleteAllOrders('orderAtCounter', 'Order at Counter')}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      Delete All Orders
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    Example: {settings.orderAtCounter.orderPrefix || 'OAC'}{settings.orderAtCounter.orderCounter || 1000}
                  </p>
                </div>
              </div>

              {/* Workflow Section */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Workflow Steps</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentWorkflowTab('orderAtCounter');
                      setEditingWorkflowStepIndex(null);
                      setWorkflowStepLabel('');
                      setShowAddWorkflowStepModal(true);
                    }}
                    className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Step
                  </button>
                </div>

                {/* Workflow Steps List */}
                <div className="space-y-3">
                  {settings.orderAtCounter.workflow && settings.orderAtCounter.workflow.length > 0 ? (
                    settings.orderAtCounter.workflow.map((step, index) => (
                      <div key={step.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-6 h-6 bg-[rgb(41,173,211)] text-white rounded-full text-xs font-semibold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{step.label}</span>
                              {step.isFirst && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">First Step</span>
                              )}
                              {step.isLast && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Last Step</span>
                              )}
                              {step.isPaymentStep && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  Payment Step
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Move Up Button - disabled for first step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepUp('orderAtCounter', index)}
                            disabled={index === 0}
                            className={`p-2 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          {/* Move Down Button - disabled for last step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepDown('orderAtCounter', index)}
                            disabled={index === settings.orderAtCounter.workflow.length - 1}
                            className={`p-2 rounded ${index === settings.orderAtCounter.workflow.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentWorkflowTab('orderAtCounter');
                              setEditingWorkflowStepIndex(index);
                              setWorkflowStepLabel(step.label);
                              setIsPaymentStep(step.isPaymentStep || false);
                              setShowAddWorkflowStepModal(true);
                            }}
                            className="p-2 text-[rgb(41,173,211)] hover:bg-[rgba(41,173,211,0.1)] rounded"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedWorkflow = settings.orderAtCounter.workflow.filter((_, i) => i !== index);
                              setSettings({
                                ...settings,
                                orderAtCounter: {
                                  ...settings.orderAtCounter,
                                  workflow: updatedWorkflow
                                }
                              });
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                            disabled={settings.orderAtCounter.workflow.length <= 1}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No workflow steps defined</p>
                  )}
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> The first step is automatically assigned when an order is created. The last step marks the order as complete.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Order at Table */}
          {activeTab === 'orderAtTable' && (
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Enable Order at Table</h3>
                  <p className="text-sm text-gray-500 mt-1">Allow customers to order directly from their table</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={settings.orderAtTable.enabled}
                    onChange={(e) => setSettings({ ...settings, orderAtTable: { ...settings.orderAtTable, enabled: e.target.checked }})}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                </label>
              </div>

              {/* Order Number Configuration */}
              <div className="py-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-4">Order Number Configuration</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prefix</label>
                      <input
                        type="text"
                        value={settings.orderAtTable.orderPrefix || 'OAT'}
                        onChange={(e) => setSettings({ ...settings, orderAtTable: { ...settings.orderAtTable, orderPrefix: e.target.value }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="OAT"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Starting Number</label>
                      <input
                        type="number"
                        value={settings.orderAtTable.orderCounter || 1000}
                        onChange={(e) => setSettings({ ...settings, orderAtTable: { ...settings.orderAtTable, orderCounter: parseInt(e.target.value) || 1000 }})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="1000"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleResetCounter('orderAtTable', 'Order at Table')}
                      className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,163,201)]"
                    >
                      Reset Counter
                    </button>
                    <button
                      onClick={() => handleDeleteAllOrders('orderAtTable', 'Order at Table')}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      Delete All Orders
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-3">
                    Example: {settings.orderAtTable.orderPrefix || 'OAT'}{settings.orderAtTable.orderCounter || 1000}
                  </p>
                </div>
              </div>

              {/* Workflow Section */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Workflow Steps</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentWorkflowTab('orderAtTable');
                      setEditingWorkflowStepIndex(null);
                      setWorkflowStepLabel('');
                      setShowAddWorkflowStepModal(true);
                    }}
                    className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Step
                  </button>
                </div>

                {/* Workflow Steps List */}
                <div className="space-y-3">
                  {settings.orderAtTable.workflow && settings.orderAtTable.workflow.length > 0 ? (
                    settings.orderAtTable.workflow.map((step, index) => (
                      <div key={step.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-6 h-6 bg-[rgb(41,173,211)] text-white rounded-full text-xs font-semibold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{step.label}</span>
                              {step.isFirst && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">First Step</span>
                              )}
                              {step.isLast && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Last Step</span>
                              )}
                              {step.isPaymentStep && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  Payment Step
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Move Up Button - disabled for first step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepUp('orderAtTable', index)}
                            disabled={index === 0}
                            className={`p-2 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          {/* Move Down Button - disabled for last step */}
                          <button
                            type="button"
                            onClick={() => moveWorkflowStepDown('orderAtTable', index)}
                            disabled={index === settings.orderAtTable.workflow.length - 1}
                            className={`p-2 rounded ${index === settings.orderAtTable.workflow.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setCurrentWorkflowTab('orderAtTable');
                              setEditingWorkflowStepIndex(index);
                              setWorkflowStepLabel(step.label);
                              setIsPaymentStep(step.isPaymentStep || false);
                              setShowAddWorkflowStepModal(true);
                            }}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this workflow step?')) {
                                const updatedWorkflow = settings.orderAtTable.workflow.filter((_, i) => i !== index);
                                setSettings({ 
                                  ...settings, 
                                  orderAtTable: { 
                                    ...settings.orderAtTable, 
                                    workflow: updatedWorkflow 
                                  } 
                                });
                              }
                            }}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            disabled={step.isFirst || step.isLast}
                            title={step.isFirst || step.isLast ? 'Cannot delete first or last step' : 'Delete step'}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No workflow steps defined</p>
                  )}
                </div>

                {/* Workflow Description */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This workflow is used when customers pay during ordering (payment first).
                  </p>
                </div>
              </div>

              {/* PIN-based Workflow Section */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Workflow Steps for PIN-based Orders</h3>
                    <p className="text-xs text-gray-600 mt-1">Used when customers order first and pay later via Pay at Table</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentWorkflowTab('orderAtTablePIN');
                      setEditingWorkflowStepIndex(null);
                      setWorkflowStepLabel('');
                      setShowAddWorkflowStepModal(true);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Step
                  </button>
                </div>

                {/* PIN Workflow Steps List */}
                <div className="space-y-3">
                  {settings.orderAtTable.workflowPIN && settings.orderAtTable.workflowPIN.length > 0 ? (
                    settings.orderAtTable.workflowPIN.map((step, index) => (
                      <div key={step.id} className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-6 h-6 bg-purple-600 text-white rounded-full text-xs font-semibold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{step.label}</span>
                              {step.isFirst && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">First Step</span>
                              )}
                              {step.isLast && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Last Step</span>
                              )}
                              {(step.label === 'Awaiting Payment' || step.isPaymentStep) && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded flex items-center gap-1">
                                  <CreditCard className="h-3 w-3" />
                                  Payment Step
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setCurrentWorkflowTab('orderAtTablePIN');
                              setEditingWorkflowStepIndex(index);
                              setWorkflowStepLabel(step.label);
                              setIsPaymentStep(step.isPaymentStep || false);
                              setShowAddWorkflowStepModal(true);
                            }}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this workflow step?')) {
                                const updatedWorkflow = settings.orderAtTable.workflowPIN.filter((_, i) => i !== index);
                                setSettings({ 
                                  ...settings, 
                                  orderAtTable: { 
                                    ...settings.orderAtTable, 
                                    workflowPIN: updatedWorkflow 
                                  } 
                                });
                              }
                            }}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            disabled={step.isFirst || step.isLast}
                            title={step.isFirst || step.isLast ? 'Cannot delete first or last step' : 'Delete step'}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No workflow steps defined</p>
                  )}
                </div>

                {/* PIN Workflow Description */}
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Auto-Transition:</strong> When order reaches "Ready" status, it automatically moves to "Awaiting Payment". When customer completes payment via Pay at Table, order automatically moves to "Completed".
                  </p>
                </div>
              </div>

              {/* Payment & PIN Info */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Choose either Payments (credit card) OR PIN verification. Only one can be enabled at a time for Order at Table.
                </p>
              </div>

              {/* Payment Settings */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Enable Payments</h3>
                    <p className="text-sm text-gray-500 mt-1">Require credit card payment at checkout</p>
                    {settings.orderAtTable.enablePIN && (
                      <p className="text-xs text-orange-600 mt-1">⚠️ Cannot enable while PIN is enabled</p>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={settings.orderAtTable.enablePayments}
                      disabled={settings.orderAtTable.enablePIN}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // If enabling payments, disable PIN
                          setSettings({ 
                            ...settings, 
                            orderAtTable: { 
                              ...settings.orderAtTable, 
                              enablePayments: true,
                              enablePIN: false 
                            }
                          });
                        } else {
                          setSettings({ 
                            ...settings, 
                            orderAtTable: { 
                              ...settings.orderAtTable, 
                              enablePayments: false
                            }
                          });
                        }
                      }}
                      className="sr-only peer" 
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)] ${settings.orderAtTable.enablePIN ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                  </label>
                </div>
              </div>

              {/* PIN Settings */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Enable PIN</h3>
                    <p className="text-sm text-gray-500 mt-1">Require PIN verification for order placement</p>
                    {settings.orderAtTable.enablePayments && (
                      <p className="text-xs text-orange-600 mt-1">⚠️ Cannot enable while Payments is enabled</p>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={settings.orderAtTable.enablePIN}
                      disabled={settings.orderAtTable.enablePayments}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // If enabling PIN, disable payments
                          setSettings({ 
                            ...settings, 
                            orderAtTable: { 
                              ...settings.orderAtTable, 
                              enablePIN: true,
                              enablePayments: false 
                            }
                          });
                        } else {
                          setSettings({ 
                            ...settings, 
                            orderAtTable: { 
                              ...settings.orderAtTable, 
                              enablePIN: false
                            }
                          });
                        }
                      }}
                      className="sr-only peer" 
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)] ${settings.orderAtTable.enablePayments ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                  </label>
                </div>

                {settings.orderAtTable.enablePIN && (
                  <div className="space-y-4">
                    {/* PIN Type Selection */}
                    <div>
                      <label className="text-sm font-medium text-gray-900 block mb-2">PIN Type</label>
                      <div className="space-y-2">
                        <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input 
                            type="radio" 
                            name="pinType" 
                            value="daily"
                            checked={settings.orderAtTable.pinType === 'daily'}
                            onChange={(e) => setSettings({ ...settings, orderAtTable: { ...settings.orderAtTable, pinType: e.target.value }})}
                            className="w-4 h-4 text-[rgb(41,173,211)] border-gray-300 focus:ring-[rgb(41,173,211)]" 
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">Daily Auto-Generate</span>
                            <p className="text-xs text-gray-500">New PIN generated automatically each day</p>
                          </div>
                        </label>
                        <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input 
                            type="radio" 
                            name="pinType" 
                            value="manual"
                            checked={settings.orderAtTable.pinType === 'manual'}
                            onChange={(e) => setSettings({ ...settings, orderAtTable: { ...settings.orderAtTable, pinType: e.target.value }})}
                            className="w-4 h-4 text-[rgb(41,173,211)] border-gray-300 focus:ring-[rgb(41,173,211)]" 
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">Manual Generate</span>
                            <p className="text-xs text-gray-500">PIN never expires until manually regenerated</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Generate PIN Button */}
                    <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Current PIN</h4>
                        {currentPIN && currentPIN.pin ? (
                          <div className="mt-2">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl font-bold text-[rgb(41,173,211)] tracking-wider">{currentPIN.pin}</span>
                              {currentPIN.expired && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">Expired</span>
                              )}
                            </div>
                            {currentPIN.type === 'daily' && !currentPIN.expired && (
                              <p className="text-xs text-gray-600 mt-1">Expires at end of day</p>
                            )}
                            {currentPIN.type === 'manual' && !currentPIN.expired && (
                              <p className="text-xs text-gray-600 mt-1">Valid until regenerated</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 mt-1">No PIN generated yet</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={generatePIN}
                        disabled={pinLoading}
                        className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {pinLoading ? 'Generating...' : (currentPIN && currentPIN.pin ? 'Regenerate PIN' : 'Generate PIN')}
                      </button>
                    </div>

                    {/* PIN Info */}
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> {settings.orderAtTable.pinType === 'daily' 
                          ? 'The PIN will be displayed in the Orders Dashboard for waiters to provide to customers.' 
                          : 'This PIN will remain valid until you manually generate a new one.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Tip Settings */}
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Enable Tip</h3>
                    <p className="text-sm text-gray-500 mt-1">Allow customers to add tips to their orders</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={settings.orderAtTable.enableTip}
                      onChange={(e) => setSettings({ ...settings, orderAtTable: { ...settings.orderAtTable, enableTip: e.target.checked }})}
                      className="sr-only peer" 
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                  </label>
                </div>

                {settings.orderAtTable.enableTip && (
                  <div className="space-y-4">
                    {/* Tip Options */}
                    <div>
                      <label className="text-sm font-medium text-gray-900 block mb-2">Tip Percentage Options</label>
                      <p className="text-xs text-gray-500 mb-3">Configure quick tip percentage options (customers can also enter custom tip amounts)</p>
                      <div className="grid grid-cols-3 gap-3">
                        {[0, 1, 2].map((index) => (
                          <div key={index}>
                            <label className="text-xs text-gray-600 block mb-1">Option {index + 1}</label>
                            <div className="relative">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={settings.orderAtTable.tipOptions[index] || ''}
                                onChange={(e) => {
                                  const newTipOptions = [...settings.orderAtTable.tipOptions];
                                  newTipOptions[index] = parseInt(e.target.value) || 0;
                                  setSettings({
                                    ...settings,
                                    orderAtTable: {
                                      ...settings.orderAtTable,
                                      tipOptions: newTipOptions
                                    }
                                  });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(41,173,211)] pr-8"
                                placeholder="0"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tip Info */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Customers will see these percentage options as quick selections. They can also choose "Custom" to enter a specific tip amount in dollars.
                      </p>
                    </div>
                  </div>
                )}

                {/* Barcode Scanning */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={settings.orderAtTable.enableBarcodeScanning}
                      onChange={(e) => setSettings({ ...settings, orderAtTable: { ...settings.orderAtTable, enableBarcodeScanning: e.target.checked }})}
                      className="sr-only peer" 
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                    <span className="ml-3 text-sm font-medium text-gray-900">Enable Barcode Scanning</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">Allow orders to be tracked and accessed via barcode scanning</p>
                  
                  {/* Barcode Management Section - Show when enabled */}
                  {settings.orderAtTable.enableBarcodeScanning && (
                    <div className="mt-4 space-y-4">
                      {/* Generate Barcodes for Tables */}
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">Barcode Management</h4>
                            <p className="text-xs text-gray-600 mt-1">Generate barcodes for all tables from Manage Tables</p>
                          </div>
                          <button 
                            onClick={async () => {
                              try {
                                setLoadingTableBarcodes(true);
                                // Fetch all tables
                                const tablesResponse = await axios.get(`${API_BASE_URL}/api/tables`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                
                                const tables = tablesResponse.data;
                                if (!tables || tables.length === 0) {
                                  showMessage('No tables found. Please create tables first in Manage Tables.', 'error');
                                  return;
                                }
                                
                                // Check for existing barcodes
                                const barcodesResponse = await axios.get(`${API_BASE_URL}/api/pay-at-table/barcodes`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                
                                const existingBarcodes = barcodesResponse.data.barcodes || [];
                                const existingTableIds = new Set(existingBarcodes.map(b => b.assigned_table_id).filter(Boolean));
                                
                                // Find tables without barcodes
                                const tablesToGenerate = tables.filter(t => !existingTableIds.has(t.id));
                                
                                if (tablesToGenerate.length === 0) {
                                  showMessage('All tables already have barcodes!', 'success');
                                  return;
                                }
                                
                                // Generate barcodes for new tables
                                const errors = [];
                                for (const table of tablesToGenerate) {
                                  try {
                                    await axios.post(`${API_BASE_URL}/api/pay-at-table/barcodes`, {}, {
                                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                    });
                                  } catch (err) {
                                    errors.push(`Failed to generate barcode for ${table.name}`);
                                  }
                                }
                                
                                if (errors.length > 0) {
                                  showMessage(`Generated barcodes with some errors:\n${errors.join('\n')}`, 'warning');
                                } else {
                                  showMessage(`Successfully generated barcodes for ${tablesToGenerate.length} new table(s)!`, 'success');
                                }
                              } catch (error) {
                                console.error('Error generating barcodes:', error);
                                showMessage('Failed to generate barcodes. Please try again.', 'error');
                              } finally {
                                setLoadingTableBarcodes(false);
                              }
                            }}
                            disabled={loadingTableBarcodes}
                            className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50 flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {loadingTableBarcodes ? 'Checking...' : 'Refresh & Generate'}
                          </button>
                        </div>
                        
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs text-blue-800">
                            <strong>How it works:</strong> Click "Refresh & Generate" to check all tables and automatically create barcodes for any new tables. Existing table barcodes will not be affected.
                          </p>
                        </div>
                      </div>
                      
                      {/* Instructions */}
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <h4 className="text-sm font-medium text-yellow-900 mb-2">Setup Instructions</h4>
                        <ol className="text-xs text-yellow-800 space-y-1 list-decimal list-inside">
                          <li>Create tables in <strong>Manage Tables</strong> page</li>
                          <li>Click <strong>Refresh & Generate</strong> to create barcodes for all tables</li>
                          <li>Assign barcodes to specific orders from <strong>Orders Dashboard</strong></li>
                          <li>Download and print barcodes</li>
                          <li>Place printed barcodes on tables for easy access</li>
                        </ol>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pay at Table */}
          {activeTab === 'payAtTable' && (
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Enable Pay at Table</h3>
                  <p className="text-sm text-gray-500 mt-1">Allow customers to pay for existing orders at their table</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={settings.payAtTable.enabled}
                    onChange={(e) => setSettings({ ...settings, payAtTable: { ...settings.payAtTable, enabled: e.target.checked }})}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                </label>
              </div>

              {settings.payAtTable.enabled && (
                <div className="space-y-6">
                  {/* Payment Options */}
                  <div className="border border-gray-200 rounded-lg p-6 bg-white">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Payment Access Options</h4>
                    
                    {/* Option 1: Table Selection */}
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer mb-3 hover:border-[rgb(41,173,211)] transition-colors"
                      style={{ borderColor: settings.payAtTable.enableTableSelection ? 'var(--theme-primary)' : '#e5e7eb' }}>
                      <input 
                        type="checkbox" 
                        checked={settings.payAtTable.enableTableSelection}
                        onChange={(e) => setSettings({ 
                          ...settings, 
                          payAtTable: { ...settings.payAtTable, enableTableSelection: e.target.checked }
                        })}
                        className="mt-1 w-4 h-4 text-[rgb(41,173,211)] border-gray-300 rounded focus:ring-[rgb(41,173,211)]" 
                      />
                      <div className="ml-3">
                        <span className="text-sm font-medium text-gray-900">Enable Table Selection</span>
                        <p className="text-xs text-gray-500 mt-1">Customers can select their table number to view and pay for their order</p>
                      </div>
                    </label>

                    {/* Option 2: Barcode Scanning */}
                    <label className="flex items-start p-4 border-2 border-gray-200 rounded-lg cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={settings.payAtTable.enableBarcodeScanning}
                        onChange={(e) => setSettings({
                          ...settings,
                          payAtTable: { ...settings.payAtTable, enableBarcodeScanning: e.target.checked }
                        })}
                        className="mt-1 w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-blue-500" 
                      />
                      <div className="ml-3">
                        <span className="text-sm font-medium text-gray-900">Enable Barcode Scanning</span>
                        <p className="text-xs text-gray-600 mt-1">Customers scan a QR code at their table to access payment</p>
                      </div>
                    </label>
                  </div>

                  {/* Barcode Management Section */}
                  {settings.payAtTable.enableBarcodeScanning && (
                    <BarcodeManagementSection />
                  )}

                  {/* Info Box */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>How it works:</strong> Customers can either select their table from the "Pay at Table" page OR scan a QR code barcode placed on their table for quick access to payment.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Shipping */}
          {activeTab === 'shipping' && (
            <div className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Enable Shipping</h3>
                  <p className="text-sm text-gray-500 mt-1">Ship products directly to customers</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={settings.shipping.enabled}
                    onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, enabled: e.target.checked }})}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                </label>
              </div>

              {settings.shipping.enabled && (
                <>
                  {/* Shipping Fee Type Section */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="mb-4">
                      <h3 className="text-base font-semibold text-gray-900">Shipping Fee Type</h3>
                      <p className="text-sm text-gray-600 mt-1">Configure how shipping fees are calculated for your orders</p>
                    </div>
                    
                  <div className="space-y-4">
                    
                    {/* Free Shipping Option */}
                    <div className="border border-gray-200 rounded-lg p-4 hover:border-[rgb(41,173,211)] transition-colors">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="shippingType"
                          value="free"
                          checked={settings.shipping.shippingType === 'free'}
                          onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, shippingType: e.target.value }})}
                          className="mt-1 h-4 w-4 text-[rgb(41,173,211)] focus:ring-[rgb(41,173,211)]"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">Free Shipping</div>
                          <div className="text-sm text-gray-500 mt-1">No shipping charges for customers</div>
                          {settings.shipping.shippingType === 'free' && (
                            <div className="mt-3">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Customer Description</label>
                              <input
                                type="text"
                                placeholder="e.g., Free shipping on all orders - Arrives in 3-5 business days"
                                value={settings.shipping.freeShippingDescription}
                                onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, freeShippingDescription: e.target.value }})}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                              />
                            </div>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* Flat Fee Shipping Option */}
                    <div className="border border-gray-200 rounded-lg p-4 hover:border-[rgb(41,173,211)] transition-colors">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="shippingType"
                          value="flat"
                          checked={settings.shipping.shippingType === 'flat'}
                          onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, shippingType: e.target.value }})}
                          className="mt-1 h-4 w-4 text-[rgb(41,173,211)] focus:ring-[rgb(41,173,211)]"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">Flat Shipping Fee</div>
                          <div className="text-sm text-gray-500 mt-1">Offer multiple shipping speed options with different prices</div>
                          {settings.shipping.shippingType === 'flat' && (
                            <div className="mt-3 space-y-3">
                              <div className="flex items-center justify-between">
                                <label className="block text-xs font-medium text-gray-700">Shipping Options</label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Initialize flatShippingOptions if it doesn't exist
                                    if (!settings.shipping.flatShippingOptions) {
                                      setSettings({
                                        ...settings,
                                        shipping: {
                                          ...settings.shipping,
                                          flatShippingOptions: []
                                        }
                                      });
                                    }
                                    setEditingFlatShippingIndex(null);
                                    setFlatShippingName('');
                                    setFlatShippingPrice(0);
                                    setFlatShippingDescription('');
                                    setShowAddFlatShippingModal(true);
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[rgb(41,173,211)] border border-[rgb(41,173,211)] rounded hover:bg-[rgba(41,173,211,0.1)]"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add Option
                                </button>
                              </div>
                              
                              {!Array.isArray(settings.shipping.flatShippingOptions) || settings.shipping.flatShippingOptions.length === 0 ? (
                                <div className="text-center py-4 bg-white rounded border border-dashed border-gray-300">
                                  <p className="text-xs text-gray-500">No shipping options added yet</p>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {settings.shipping.flatShippingOptions.map((option, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded border border-gray-200">
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">{option.name}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">{option.description}</div>
                                        <div className="text-xs text-[rgb(41,173,211)] font-semibold mt-1">${option.price.toFixed(2)}</div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditingFlatShippingIndex(index);
                                            setFlatShippingName(option.name);
                                            setFlatShippingPrice(option.price);
                                            setFlatShippingDescription(option.description);
                                            setShowAddFlatShippingModal(true);
                                          }}
                                          className="p-1 text-theme-blue hover:bg-blue-50 rounded"
                                        >
                                          <Edit2 className="h-4 w-4" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updated = settings.shipping.flatShippingOptions.filter((_, i) => i !== index);
                                            setSettings({
                                              ...settings,
                                              shipping: {
                                                ...settings.shipping,
                                                flatShippingOptions: updated
                                              }
                                            });
                                          }}
                                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* Category-Based Shipping Option */}
                    <div className="border border-gray-200 rounded-lg p-4 hover:border-[rgb(41,173,211)] transition-colors">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="shippingType"
                          value="category"
                          checked={settings.shipping.shippingType === 'category'}
                          onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, shippingType: e.target.value }})}
                          className="mt-1 h-4 w-4 text-[rgb(41,173,211)] focus:ring-[rgb(41,173,211)]"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">Category-Based Shipping</div>
                          <div className="text-sm text-gray-500 mt-1">Set different shipping fees for different product categories</div>
                          {settings.shipping.shippingType === 'category' && (
                            <div className="mt-3 space-y-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Customer Description</label>
                                <input
                                  type="text"
                                  placeholder="e.g., Shipping fee varies by product category - Arrives in 3-5 business days"
                                  value={settings.shipping.categoryShippingDescription}
                                  onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, categoryShippingDescription: e.target.value }})}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                                />
                              </div>

                              {/* Default Shipping for Uncategorized Products */}
                              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                                <label className="block text-xs font-semibold text-gray-900 mb-3">
                                  Default Shipping for Uncategorized Products
                                </label>
                                <p className="text-xs text-gray-600 mb-3">
                                  Choose the shipping method for products that don't belong to any configured category
                                </p>
                                
                                <div className="space-y-3">
                                  {/* Free as Default */}
                                  <label className="flex items-center cursor-pointer">
                                    <input
                                      type="radio"
                                      name="categoryDefaultType"
                                      value="free"
                                      checked={settings.shipping.categoryDefaultType === 'free'}
                                      onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, categoryDefaultType: e.target.value }})}
                                      className="h-4 w-4 text-[rgb(41,173,211)] focus:ring-[rgb(41,173,211)]"
                                    />
                                    <span className="ml-2 text-sm text-gray-900">Free Shipping (for uncategorized products)</span>
                                  </label>

                                  {/* Flat Fee as Default */}
                                  <div>
                                    <label className="flex items-center cursor-pointer">
                                      <input
                                        type="radio"
                                        name="categoryDefaultType"
                                        value="flat"
                                        checked={settings.shipping.categoryDefaultType === 'flat'}
                                        onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, categoryDefaultType: e.target.value }})}
                                        className="h-4 w-4 text-[rgb(41,173,211)] focus:ring-[rgb(41,173,211)]"
                                      />
                                      <span className="ml-2 text-sm text-gray-900">Flat Fee (for uncategorized products)</span>
                                    </label>
                                    {settings.shipping.categoryDefaultType === 'flat' && (
                                      <div className="mt-2 ml-6">
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          placeholder="0.00"
                                          value={settings.shipping.categoryDefaultFee}
                                          onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, categoryDefaultFee: parseFloat(e.target.value) || 0 }})}
                                          className="w-32 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                                        />
                                        <span className="ml-2 text-xs text-gray-500">Default fee amount</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Category Shipping List */}
                              <div className="mt-4">
                                <div className="flex items-center justify-between mb-3">
                                  <label className="block text-xs font-medium text-gray-700">Category Shipping Rates</label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowAddCategoryShippingModal(true);
                                      setSelectedParentCategory('');
                                      setSelectedProductCategory('');
                                      setCategoryShippingFee(0);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-[rgb(41,173,211)] border border-[rgb(41,173,211)] rounded-lg hover:bg-[rgba(41,173,211,0.1)]"
                                  >
                                    <Plus className="h-3 w-3" />
                                    Add Category
                                  </button>
                                </div>
                                
                                {settings.shipping.categoryShipping.length === 0 ? (
                                  <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                    <p className="text-sm text-gray-500">No category shipping rates added yet</p>
                                    <p className="text-xs text-gray-400 mt-1">Click "Add Category" to configure shipping rates</p>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {settings.shipping.categoryShipping.map((item, index) => (
                                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                        <div>
                                          <div className="text-sm font-medium text-gray-900">
                                            {item.productCategory || item.categoryName || item.category}
                                            {item.menuCategoryName && (
                                              <span className="text-xs text-gray-500 ml-2">
                                                (in {item.menuCategoryName})
                                              </span>
                                            )}
                                          </div>
                                          <div className="text-xs text-[rgb(41,173,211)] font-semibold mt-1">
                                            ${item.fee.toFixed(2)} shipping fee (overrides default)
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updated = settings.shipping.categoryShipping.filter((_, i) => i !== index);
                                            setSettings({
                                              ...settings,
                                              shipping: {
                                                ...settings.shipping,
                                                categoryShipping: updated
                                              }
                                            });
                                          }}
                                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                  </div>

                  {/* Countries & States Configuration */}
                  <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Shipping Countries & States</h3>
                        <p className="text-xs text-gray-500 mt-1">Configure countries and states/regions available for shipping</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddCountryModal(true)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]"
                      >
                        <Plus className="h-3 w-3" />
                        Add Country
                      </button>
                    </div>

                    {/* Countries List - Accordion Style */}
                    <div className="space-y-2">
                      {settings.shipping.countries.map((country, countryIndex) => {
                        const isOpen = openCountryAccordion === countryIndex;
                        return (
                          <div key={countryIndex} className={`border rounded-lg overflow-hidden transition-all duration-200 ${country.enabled ? 'border-gray-200 bg-white' : 'border-gray-300 bg-gray-100 opacity-60'}`}>
                            {/* Accordion Header */}
                            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                              onClick={() => setOpenCountryAccordion(isOpen ? null : countryIndex)}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <ChevronDown className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                                <div className="flex items-center gap-3">
                                  <h4 className="text-sm font-semibold text-gray-900">{country.name}</h4>
                                  {!country.enabled && <span className="text-xs text-gray-500 italic">(Disabled)</span>}
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                    {country.states.filter(s => s.enabled).length} / {country.states.length} states
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedCountryIndex(countryIndex);
                                    setShowAddStateModal(true);
                                  }}
                                  disabled={!country.enabled}
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[rgb(41,173,211)] border border-[rgb(41,173,211)] rounded hover:bg-[rgba(41,173,211,0.1)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add State
                                </button>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    checked={country.enabled}
                                    onChange={(e) => {
                                      const updatedCountries = [...settings.shipping.countries];
                                      updatedCountries[countryIndex].enabled = e.target.checked;
                                      setSettings({
                                        ...settings,
                                        shipping: {
                                          ...settings.shipping,
                                          countries: updatedCountries
                                        }
                                      });
                                    }}
                                    className="sr-only peer" 
                                  />
                                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                                </label>
                              </div>
                            </div>

                            {/* Accordion Content - States List */}
                            <div 
                              className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
                              style={{ overflow: 'hidden' }}
                            >
                              <div className="px-4 pb-4 pt-2 border-t border-gray-200 bg-gray-50">
                                <p className="text-xs font-medium text-gray-700 mb-3">
                                  States/Regions ({country.states.filter(s => s.enabled).length} enabled / {country.states.length} total)
                                </p>
                                {country.states.length === 0 ? (
                                  <div className="text-center py-4 bg-white rounded border border-dashed border-gray-300">
                                    <p className="text-xs text-gray-500">No states added yet</p>
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                    {country.states.map((state, stateIndex) => (
                                      <div key={stateIndex} className={`flex items-center justify-between px-2 py-1.5 rounded border text-xs transition-all duration-200 ${state.enabled ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300 opacity-60'}`}>
                                        <span className={`truncate ${state.enabled ? 'text-gray-700' : 'text-gray-500'}`}>
                                          {state.name}
                                        </span>
                                        <label className="relative inline-flex items-center cursor-pointer ml-1">
                                          <input 
                                            type="checkbox" 
                                            checked={state.enabled}
                                            disabled={!country.enabled}
                                            onChange={(e) => {
                                              const updatedCountries = [...settings.shipping.countries];
                                              updatedCountries[countryIndex].states[stateIndex].enabled = e.target.checked;
                                              setSettings({
                                                ...settings,
                                                shipping: {
                                                  ...settings.shipping,
                                                  countries: updatedCountries
                                                }
                                              });
                                            }}
                                            className="sr-only peer" 
                                          />
                                          <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[rgb(41,173,211)] peer-disabled:opacity-50"></div>
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {settings.shipping.countries.length === 0 && (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                          <p className="text-sm text-gray-500">No countries configured yet</p>
                          <p className="text-xs text-gray-400 mt-1">Click "Add Country" to start</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Order Number Configuration Section */}
                  <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                    <div className="mb-4">
                      <h3 className="text-base font-semibold text-gray-900">Order Number Configuration</h3>
                      <p className="text-sm text-gray-600 mt-1">Configure order number prefix and counter for shipping orders</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Order Number Prefix</label>
                        <input
                          type="text"
                          placeholder="e.g., SHIP"
                          value={settings.shipping.orderNumberPrefix || ''}
                          onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, orderNumberPrefix: e.target.value }})}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Counter Start From</label>
                        <input
                          type="number"
                          placeholder="1000"
                          value={settings.shipping.orderNumberStartFrom || ''}
                          onChange={(e) => setSettings({ ...settings, shipping: { ...settings.shipping, orderNumberStartFrom: parseInt(e.target.value) || 1000 }})}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to reset the counter? The next order will start from the "Counter Start From" number.')) {
                            setSettings({
                              ...settings,
                              shipping: {
                                ...settings.shipping,
                                currentOrderCounter: settings.shipping.orderNumberStartFrom || 1000
                              }
                            });
                            showMessage('Counter reset successfully!', 'success');
                          }
                        }}
                        className="px-4 py-2 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600"
                      >
                        Reset Counter
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAllOrders('shipping', 'Shipping')}
                        className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600"
                      >
                        Delete All Orders
                      </button>
                    </div>
                  </div>

                  {/* Workflow Section */}
                  <div className="mt-6 bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Workflow Steps</h3>
                        <p className="text-sm text-gray-600 mt-1">Define the status workflow for shipping orders</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentWorkflowTab('shipping');
                          setEditingWorkflowStepIndex(null);
                          setWorkflowStepLabel('');
                          setShowAddWorkflowStepModal(true);
                        }}
                        className="px-4 py-2 bg-[rgb(41,173,211)] text-white text-sm font-medium rounded-lg hover:bg-[rgb(31,153,191)] flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Step
                      </button>
                    </div>

                    {/* Workflow Steps List */}
                    <div className="space-y-3">
                      {settings.shipping.workflow && settings.shipping.workflow.length > 0 ? (
                        settings.shipping.workflow.map((step, index) => (
                          <div key={step.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center justify-center w-6 h-6 bg-[rgb(41,173,211)] text-white rounded-full text-xs font-semibold">
                                {index + 1}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">{step.label}</span>
                                  {step.isFirst && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">First Step</span>
                                  )}
                                  {step.isLast && (
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Last Step</span>
                                  )}
                                  {step.isPaymentStep && (
                                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded flex items-center gap-1">
                                      <CreditCard className="h-3 w-3" />
                                      Payment Step
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Move Up Button - disabled for first step */}
                              <button
                                type="button"
                                onClick={() => moveWorkflowStepUp('shipping', index)}
                                disabled={index === 0}
                                className={`p-2 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                                title="Move up"
                              >
                                <ChevronUp className="h-4 w-4" />
                              </button>
                              {/* Move Down Button - disabled for last step */}
                              <button
                                type="button"
                                onClick={() => moveWorkflowStepDown('shipping', index)}
                                disabled={index === settings.shipping.workflow.length - 1}
                                className={`p-2 rounded ${index === settings.shipping.workflow.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                                title="Move down"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setCurrentWorkflowTab('shipping');
                                  setEditingWorkflowStepIndex(index);
                                  setWorkflowStepLabel(step.label);
                              setIsPaymentStep(step.isPaymentStep || false);
                                  setShowAddWorkflowStepModal(true);
                                }}
                                className="p-2 text-[rgb(41,173,211)] hover:bg-[rgba(41,173,211,0.1)] rounded"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedWorkflow = settings.shipping.workflow.filter((_, i) => i !== index);
                                  setSettings({
                                    ...settings,
                                    shipping: {
                                      ...settings.shipping,
                                      workflow: updatedWorkflow
                                    }
                                  });
                                }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded"
                                disabled={settings.shipping.workflow.length <= 1}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 text-center py-3">No workflow steps defined</p>
                      )}
                    </div>

                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-[10px] text-yellow-800">
                        <strong>Note:</strong> The first step is automatically assigned when an order is created. The last step marks the order as complete.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

              {/* Save Button */}
              <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={saveSettings}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Add Delivery Area Modal */}
      {showAddAreaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Delivery Area</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddAreaModal(false);
                  setNewArea({ cityName: '', zipCodes: [] });
                  setNewZipCode('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* City Name */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  City Name
                </label>
                <input
                  type="text"
                  placeholder="Enter city name (e.g., New York, Los Angeles)"
                  value={newArea.cityName}
                  onChange={(e) => setNewArea({ ...newArea, cityName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                />
              </div>

              {/* Zip Codes */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Zip Codes
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Add all zip codes where you offer delivery service for this city.
                </p>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    placeholder="Enter zip code (e.g., 12345)"
                    value={newZipCode}
                    onChange={(e) => setNewZipCode(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddZipCode();
                      }
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleAddZipCode}
                    className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                {/* Zip Code List */}
                {newArea.zipCodes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newArea.zipCodes.map((zip, index) => (
                      <div key={index} className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full">
                        <span className="text-sm text-gray-700">{zip}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveZipCode(zip)}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowAddAreaModal(false);
                  setNewArea({ cityName: '', zipCodes: [] });
                  setNewZipCode('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveArea}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]"
              >
                Save Area
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Pickup Location Modal */}
      {showAddLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingLocation !== null ? 'Edit Pickup Location' : 'Add Pickup Location'}
              </h3>
              <button
                onClick={() => {
                  setShowAddLocationModal(false);
                  setLocationForm({ name: '', address: '', phone: '' });
                  setEditingLocation(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Location Name */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Location Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Main Store, Downtown Branch"
                  value={locationForm.name}
                  onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Address *
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g., 123 Main St, New York, NY 10001"
                  value={locationForm.address}
                  onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Phone Number (optional)
                </label>
                <input
                  type="tel"
                  placeholder="e.g., (555) 123-4567"
                  value={locationForm.phone}
                  onChange={(e) => setLocationForm({ ...locationForm, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddLocationModal(false);
                  setLocationForm({ name: '', address: '', phone: '' });
                  setEditingLocation(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveLocation}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]"
              >
                {editingLocation !== null ? 'Update Location' : 'Add Location'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Country Modal */}
      {showAddCountryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Country</h3>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Country Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Canada, United Kingdom"
                  value={newCountryName}
                  onChange={(e) => setNewCountryName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddCountryModal(false);
                  setNewCountryName('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (newCountryName.trim()) {
                    setSettings({
                      ...settings,
                      shipping: {
                        ...settings.shipping,
                        countries: [
                          ...settings.shipping.countries,
                          { name: newCountryName.trim(), enabled: true, states: [] }
                        ]
                      }
                    });
                    setShowAddCountryModal(false);
                    setNewCountryName('');
                  }
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]"
              >
                Add Country
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Flat Shipping Option Modal */}
      {showAddFlatShippingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-gray-200 bg-[rgb(41,173,211)]">
              <h3 className="text-lg font-semibold text-white">
                {editingFlatShippingIndex !== null ? 'Edit Flat Shipping Option' : 'Add Flat Shipping Option'}
              </h3>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Shipping Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Regular Shipping, Express Shipping"
                  value={flatShippingName}
                  onChange={(e) => setFlatShippingName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Price ($) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={flatShippingPrice}
                  onChange={(e) => setFlatShippingPrice(parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Description *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Arrives in 3-5 business days"
                  value={flatShippingDescription}
                  onChange={(e) => setFlatShippingDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddFlatShippingModal(false);
                  setEditingFlatShippingIndex(null);
                  setFlatShippingName('');
                  setFlatShippingPrice(0);
                  setFlatShippingDescription('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!flatShippingName.trim() || flatShippingPrice === '' || !flatShippingDescription.trim()}
                onClick={() => {
                  if (flatShippingName.trim() && flatShippingDescription.trim()) {
                    const existingOptions = Array.isArray(settings.shipping.flatShippingOptions) 
                      ? settings.shipping.flatShippingOptions 
                      : [];
                    
                    if (editingFlatShippingIndex !== null) {
                      // Edit existing option
                      const updatedOptions = [...existingOptions];
                      updatedOptions[editingFlatShippingIndex] = {
                        name: flatShippingName.trim(),
                        price: parseFloat(flatShippingPrice) || 0,
                        description: flatShippingDescription.trim()
                      };
                      setSettings({
                        ...settings,
                        shipping: {
                          ...settings.shipping,
                          flatShippingOptions: updatedOptions
                        }
                      });
                    } else {
                      // Add new option
                      setSettings({
                        ...settings,
                        shipping: {
                          ...settings.shipping,
                          flatShippingOptions: [
                            ...existingOptions,
                            {
                              name: flatShippingName.trim(),
                              price: parseFloat(flatShippingPrice) || 0,
                              description: flatShippingDescription.trim()
                            }
                          ]
                        }
                      });
                    }
                    
                    setShowAddFlatShippingModal(false);
                    setEditingFlatShippingIndex(null);
                    setFlatShippingName('');
                    setFlatShippingPrice(0);
                    setFlatShippingDescription('');
                  }
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingFlatShippingIndex !== null ? 'Update Option' : 'Add Option'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Shipping Modal */}
      {showAddCategoryShippingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-gray-200 bg-[rgb(41,173,211)]">
              <h3 className="text-lg font-semibold text-white">Add Category Shipping Rate</h3>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Menu Category Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Menu Category *
                </label>
                <select
                  value={selectedParentCategory}
                  onChange={(e) => {
                    setSelectedParentCategory(e.target.value);
                    setSelectedProductCategory(''); // Reset product category when parent changes
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                >
                  <option value="">Select Menu Category</option>
                  {menuCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Product Category Dropdown - Shows when Menu Category is selected */}
              {selectedParentCategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Product Category *
                  </label>
                  <select
                    value={selectedProductCategory}
                    onChange={(e) => setSelectedProductCategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                  >
                    <option value="">Select Product Category</option>
                    {menuCategories
                      .find(cat => cat.id === selectedParentCategory)
                      ?.productCategories?.map((productCat) => (
                        <option key={productCat} value={productCat}>
                          {productCat}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* Confirmation - Shows when both categories are selected */}
              {selectedParentCategory && selectedProductCategory && (
                <div>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      Setting custom shipping rate for <span className="font-semibold">{selectedProductCategory}</span> products in the <span className="font-semibold">{menuCategories.find(cat => cat.id === selectedParentCategory)?.name}</span> category.
                    </p>
                  </div>
                </div>
              )}

              {/* Shipping Fee Input - Shows only when both categories are selected */}
              {selectedParentCategory && selectedProductCategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Shipping Fee ($) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={categoryShippingFee}
                    onChange={(e) => setCategoryShippingFee(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This shipping fee will override the default free/flat shipping for products in this category
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddCategoryShippingModal(false);
                  setSelectedParentCategory('');
                  setSelectedProductCategory('');
                  setCategoryShippingFee(0);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedParentCategory || !selectedProductCategory || categoryShippingFee === ''}
                onClick={() => {
                  if (selectedParentCategory && selectedProductCategory) {
                    const category = menuCategories.find(cat => cat.id === selectedParentCategory);
                    
                    if (category) {
                      setSettings({
                        ...settings,
                        shipping: {
                          ...settings.shipping,
                          categoryShipping: [
                            ...settings.shipping.categoryShipping,
                            {
                              menuCategoryId: selectedParentCategory,
                              menuCategoryName: category.name,
                              productCategory: selectedProductCategory,
                              fee: parseFloat(categoryShippingFee) || 0
                            }
                          ]
                        }
                      });
                      setShowAddCategoryShippingModal(false);
                      setSelectedParentCategory('');
                      setSelectedProductCategory('');
                      setCategoryShippingFee(0);
                    }
                  }
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add State Modal */}
      {showAddStateModal && selectedCountryIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Add State to {settings.shipping.countries[selectedCountryIndex].name}
              </h3>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  State/Region Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., California, Ontario"
                  value={newStateName}
                  onChange={(e) => setNewStateName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddStateModal(false);
                  setSelectedCountryIndex(null);
                  setNewStateName('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (newStateName.trim()) {
                    const updatedCountries = [...settings.shipping.countries];
                    updatedCountries[selectedCountryIndex].states.push({ name: newStateName.trim(), enabled: true });
                    setSettings({
                      ...settings,
                      shipping: {
                        ...settings.shipping,
                        countries: updatedCountries
                      }
                    });
                    setShowAddStateModal(false);
                    setSelectedCountryIndex(null);
                    setNewStateName('');
                  }
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]"
              >
                Add State
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Workflow Step Modal */}
      {showAddWorkflowStepModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-gray-200 bg-[rgb(41,173,211)]">
              <h3 className="text-lg font-semibold text-white">
                {editingWorkflowStepIndex !== null ? 'Edit Workflow Step' : 'Add Workflow Step'}
              </h3>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Step Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., In Transit, Processing, Ready for Pickup"
                  value={workflowStepLabel}
                  onChange={(e) => setWorkflowStepLabel(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="paymentStepCheckbox"
                  checked={isPaymentStep}
                  onChange={(e) => setIsPaymentStep(e.target.checked)}
                  className="mt-1 h-4 w-4 text-[rgb(41,173,211)] border-gray-300 rounded focus:ring-[rgb(41,173,211)]"
                />
                <div className="flex-1">
                  <label htmlFor="paymentStepCheckbox" className="block text-sm font-medium text-gray-900 cursor-pointer">
                    Payment Step
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    When enabled, orders in this step will be available in "Pay at Table" and automatically complete upon payment
                  </p>
                </div>
              </div>

              {editingWorkflowStepIndex === null && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    The new step will be added before the last step. You can reorder steps by deleting and re-adding them.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddWorkflowStepModal(false);
                  setEditingWorkflowStepIndex(null);
                  setWorkflowStepLabel('');
                  setIsPaymentStep(false);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!workflowStepLabel.trim()}
                onClick={() => {
                  // Determine if we're editing PIN workflow or regular workflow
                  const isPINWorkflow = currentWorkflowTab === 'orderAtTablePIN';
                  const actualTab = isPINWorkflow ? 'orderAtTable' : currentWorkflowTab;
                  const workflowKey = isPINWorkflow ? 'workflowPIN' : 'workflow';
                  
                  const tabSettings = settings[actualTab];
                  
                  // Safety check - ensure workflow exists
                  if (!tabSettings || !tabSettings[workflowKey]) {
                    console.error('Workflow not found for tab:', currentWorkflowTab);
                    return;
                  }
                  
                  if (editingWorkflowStepIndex !== null) {
                    // Edit existing step
                    const updatedWorkflow = [...tabSettings[workflowKey]];
                    updatedWorkflow[editingWorkflowStepIndex] = {
                      ...updatedWorkflow[editingWorkflowStepIndex],
                      label: workflowStepLabel.trim(),
                      isPaymentStep: isPaymentStep
                    };
                    
                    setSettings({
                      ...settings,
                      [actualTab]: {
                        ...settings[actualTab],
                        [workflowKey]: updatedWorkflow
                      }
                    });
                  } else {
                    // Add new step (insert before last step)
                    const existingWorkflow = [...tabSettings[workflowKey]];
                    const lastStep = existingWorkflow[existingWorkflow.length - 1];
                    const beforeLast = existingWorkflow.slice(0, -1);
                    
                    // Update previous steps to not be last
                    const updatedBeforeLast = beforeLast.map(step => ({
                      ...step,
                      isLast: false
                    }));
                    
                    const newStep = {
                      id: Date.now().toString(),
                      label: workflowStepLabel.trim(),
                      isFirst: false,
                      isLast: false,
                      isPaymentStep: isPaymentStep
                    };
                    
                    const updatedWorkflow = [...updatedBeforeLast, newStep, lastStep];
                    
                    setSettings({
                      ...settings,
                      [actualTab]: {
                        ...settings[actualTab],
                        [workflowKey]: updatedWorkflow
                      }
                    });
                  }
                  
                  setShowAddWorkflowStepModal(false);
                  setEditingWorkflowStepIndex(null);
                  setWorkflowStepLabel('');
                  setIsPaymentStep(false);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingWorkflowStepIndex !== null ? 'Update Step' : 'Add Step'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-3">{confirmTitle}</h3>
              <p className="text-sm text-gray-600 mb-5">{confirmMessage}</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setConfirmAction(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmAction && confirmAction()}
                  className="px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)] transition-colors"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ECommerceSettings;
